function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

/*function initaddonUI() {
  
  let CUSTOM_UNIT_PRICE = 0; 
  const ROW = '.addon-block';
  const SELECT = 'select';
  const QTY = '.addon-quantity';
  const CUSTOM_GROUP = '.manual-size-wrapper';
  const CUSTOM_INPUTS = '.manual-size-wrapper input';
  const DISPLAY = '.addon-pillow-custom > span';
  const BTN = '.addon_addtocart';

  // One static variant id for ALL addons — replace with your real id
  const STATIC_VARIANT_ID = 44285506355339;

  // Static price when ALL custom inputs are filled
  //const CUSTOM_UNIT_PRICE = 80;

  const parseNum = (v, d=0) => {
    const n = parseFloat(String(v).replace(/,/g, ''));
    return Number.isFinite(n) ? n : d;
  };

  function getRow(el){ return el.closest(ROW); }
  function getSelect(row){ return row?.querySelector(SELECT) || null; }
  function getQtyEl(row){ return row?.querySelector(QTY) || null; }
  function getBtn(row){ return row?.querySelector(BTN) || null; }
  function getCustomInputs(row){
    const g = row?.querySelector(CUSTOM_GROUP);
    return g ? Array.from(g.querySelectorAll('input')) : [];
  }

  function customHasValue(row){
    return getCustomInputs(row).some(inp => String(inp.value || '').trim() !== '');
  }
  function customAllFilled(row){
    const inputs = getCustomInputs(row);
    if (inputs.length === 0) return false;
    return inputs.every(inp => String(inp.value || '').trim() !== '');
  }
  function selectHasValue(row){
    const sel = getSelect(row);
    return !!(sel && sel.value && sel.selectedIndex > -1);
  }

  function clearSelectSilently(row){
    const sel = getSelect(row);
    if (sel && sel.selectedIndex !== 0) sel.selectedIndex = 0;
  }
  function clearCustomInputsSilently(row){
    const inputs = getCustomInputs(row);
    inputs.forEach(inp => { if ((inp.value || '').trim() !== '') inp.value = ''; });
  }

  // Unit price: custom-filled => 80, else from selected option (data-price)
  function getUnitPrice(row){
    if (customAllFilled(row)) return CUSTOM_UNIT_PRICE;
    const sel = getSelect(row);
    const opt = sel?.options?.[sel.selectedIndex];
    return parseNum(opt?.dataset?.price, 0);
  }
  function getQty(row){
    return parseNum(getQtyEl(row)?.value, 0);
  }

  function updateTotal(row){
    const out = row?.querySelector(DISPLAY);
    if (!out) return;
    const total = (getUnitPrice(row) * getQty(row)).toFixed(2);
    out.textContent = total;
  }

  // ---- Enable/disable button logic ----
  function isSectionValid(row){
    const qtyOk = getQty(row) > 0;
    const choiceOk = selectHasValue(row) || customAllFilled(row);
    return qtyOk && choiceOk;
  }
  function applyBtnState(row){
    const btn = getBtn(row);
    if (!btn) return;
    if (isSectionValid(row)) {
      btn.disabled = false;
      btn.classList.remove('is-disabled');
    } else {
      btn.disabled = true;
      btn.classList.add('is-disabled');
    }
  }

  // --- Helpers for properties ---
  function getAddonType(row){
    if (row.classList.contains('addon-bedsheet')) return 'Bedsheet';
    if (row.classList.contains('addon-pillow'))   return 'Pillow';
    return row.querySelector('h4')?.textContent?.trim() || 'Addon';
  }
  function getSizeFromRow(row){
    if (customAllFilled(row)) {
      const vals = getCustomInputs(row).map(i => String(i.value || '').trim()).filter(Boolean);
      return vals.length >= 2 ? `${vals[0]}x${vals[1]}` : vals.join('x');
    } else {
      const sel = getSelect(row);
      if (!sel || sel.selectedIndex < 0) return '';
      const opt = sel.options[sel.selectedIndex];
      return (opt?.value || '').trim() || (opt?.textContent || '').trim();
    }
  }

  async function addAddonToCart({ id, quantity, properties }) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: Number(id), quantity: Number(quantity), properties })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.description || `Add failed (${res.status})`);
    }
    return res.json();
  }

  // Initialize all rows: compute total + disable/enable button
  document.querySelectorAll(ROW).forEach(row => {
    updateTotal(row);
    applyBtnState(row);
  });

  // Guard to avoid re-entrancy loops
  function withGuard(row, fn){
    if (!row || row.dataset._updating === '1') return;
    row.dataset._updating = '1';
    try { fn(); } finally { delete row.dataset._updating; }
  }

  // Change listeners
  document.addEventListener('change', function (e) {
    const row = getRow(e.target);
    if (!row) return;

    if (e.target.matches(SELECT)) {
      withGuard(row, () => {
        if (customHasValue(row)) clearCustomInputsSilently(row);
        updateTotal(row);
        applyBtnState(row);
      });
      return;
    }

    if (e.target.matches(QTY)) {
      //updateTotal(row);
      applyBtnState(row);
      return;
    }

    if (e.target.matches(CUSTOM_INPUTS)) {
      withGuard(row, () => {
        if (selectHasValue(row)) clearSelectSilently(row);
        //updateTotal(row);
        applyBtnState(row);
      });
    }
  });

  // Input listeners (typing)
  document.addEventListener('input', function (e) {
    const row = getRow(e.target);
    if (!row) return;

    if (e.target.matches(CUSTOM_INPUTS)) {
      withGuard(row, () => {
        if (selectHasValue(row)) clearSelectSilently(row);
        //updateTotal(row);
        applyBtnState(row);
      });
    } else if (e.target.matches(QTY)) {
      //updateTotal(row);
      applyBtnState(row);
    }
  });

  // ---- Add to Cart (static variant id + properties incl. unit price) ----
  document.addEventListener('click', async function (e) {
    const btn = e.target.closest(BTN);
    if (!btn) return;

    const row = getRow(btn);
    if (!row) return;

    if (!isSectionValid(row)) {
      alert('Please enter a quantity and choose a size or fill custom size.');
      return;
    }

    const addonType = getAddonType(row);
    const qty       = getQty(row);
    const size      = getSizeFromRow(row);
    const unitPrice = getUnitPrice(row).toFixed(2); // <-- single unit price

    const properties = {
      'Addon Type': addonType,
      'Size': size,
      '_price-total-sheet': unitPrice            // <-- added per your request
    };

    //console.log(properties);
    //return false;
    // Optional: include total too
    // const display = row.querySelector(DISPLAY);
    // if (display) properties['Addon Total'] = display.textContent.trim();

    const wasDisabled = btn.disabled;
    btn.disabled = true;
    btn.classList.add('is-disabled');

    try {
      await addAddonToCart({ id: STATIC_VARIANT_ID, quantity: qty, properties });
      // e.g. open cart drawer or toast here
    } catch (err) {
      console.error(err);
      alert(err.message || 'Unable to add addon');
    } finally {
      btn.disabled = wasDisabled;
      if (!wasDisabled) btn.classList.remove('is-disabled');
      window.location.href = '/cart';
    }
  });
}*/

// ═══════════════════════════════════════════════════════════
// TRANSLATION MESSAGES — edit translations here
// ═══════════════════════════════════════════════════════════
const CUSTOM_MESSAGES = (function() {
  const lang = (document.documentElement.lang || 'en').toLowerCase().slice(0, 2);
  const messages = {
    en: {
      width_too_large: 'Width too large for available fabric',
      multi_piece: 'Important size note',
      multi_piece_agree: 'I understand that extra fabric panels may be used where needed; the sleeping surface, however, will remain seamless.',
      dont_show: 'Don\'t show this message again',
      btn_continue: 'Continue',
      btn_cancel: 'Cancel'
    },
    fr: {
      width_too_large: 'Largeur trop grande pour le tissu disponible',
      multi_piece: 'Note importante sur la taille',
      multi_piece_agree: 'Je comprends que des panneaux de tissu supplémentaires peuvent être utilisés si nécessaire ; la surface de couchage restera toutefois sans coutures.',
      dont_show: 'Ne plus afficher ce message',
      btn_continue: 'Continuer',
      btn_cancel: 'Annuler'
    },
    de: {
      width_too_large: 'Breite zu groß für den verfügbaren Stoff',
      multi_piece: 'Wichtiger Hinweis zur Größe',
      multi_piece_agree: 'Ich verstehe, dass zusätzliche Stoffbahnen verwendet werden können; die Schlaffläche bleibt jedoch nahtlos.',
      dont_show: 'Diese Meldung nicht mehr anzeigen',
      btn_continue: 'Weiter',
      btn_cancel: 'Abbrechen'
    },
    it: {
      width_too_large: 'Larghezza troppo grande per il tessuto disponibile',
      multi_piece: 'Nota importante sulla taglia',
      multi_piece_agree: 'Capisco che potrebbero essere utilizzati pannelli di tessuto aggiuntivi dove necessario; la superficie per dormire rimarrà comunque priva di cuciture.',
      dont_show: 'Non mostrare più questo messaggio',
      btn_continue: 'Continua',
      btn_cancel: 'Annulla'
    },
    nl: {
      width_too_large: 'Breedte te groot voor beschikbare stof',
      multi_piece: 'Belangrijke maatopmerking',
      multi_piece_agree: 'Ik begrijp dat er extra stofdelen kunnen worden gebruikt waar nodig; het slaapoppervlak blijft echter naadloos.',
      dont_show: 'Dit bericht niet meer tonen',
      btn_continue: 'Doorgaan',
      btn_cancel: 'Annuleren'
    }
  };
  return messages[lang] || messages['en'];
})();
// ═══════════════════════════════════════════════════════════

// Function to get value from span or fallback to default
function getValue(selector, defaultValue) {
  const el = document.querySelector(selector);
  const value = el ? parseFloat(el.textContent.trim()) : NaN;
  return isNaN(value) ? defaultValue : value;
}


function initaddonUI() {

  const unitCheck = document.querySelector('input[name="dimension_unit"]:checked')?.value || 'in';
  document.querySelectorAll('.dimension-unit-show').forEach(function (el) {
    el.textContent = unitCheck.toLowerCase() + '.';
  });

    
  console.log(getValue('.max_roll_width'), 'tester')
  const E = getValue('.non_usable_portion', 10);
  const D = getValue('.max_roll_width', 500);
  const M = getValue('.underside_width', 22);
  const N = getValue('.factor', 2);

  const ROW = '.addon-block';
  const SELECT = 'select';
  const QTY = '.addon-quantity';
  const CUSTOM_GROUP = '.manual-size-wrapper';
  const CUSTOM_INPUTS = '.manual-size-wrapper input';
  const DISPLAY = '.price-for-this-group-custom span';
  const BTN = '.addon_addtocart';
  const STATIC_VARIANT_ID = 44285506355339;

  // ---- Helpers ----
  const parseNum = (v, d = 0) => {
    const n = parseFloat(String(v).replace(/,/g, ''));
    return Number.isFinite(n) ? n : d;
  };
  const getRow = el => el.closest(ROW);
  const getSelect = row => row?.querySelector(SELECT);
  const getQtyEl = row => row?.querySelector(QTY);
  const getBtn = row => row?.querySelector(BTN);
  const getCustomInputs = row =>
    Array.from(row?.querySelectorAll(`${CUSTOM_GROUP} input`) || []);

  // const inchesToCm = (inch) => inch * 2.54;
  let inchesToCm = (value) => {
    console.log("valueeeee", value);
    const unit =
      document.querySelector('input[name="dimension_unit"]:checked')?.value || 'in';

    console.log(unit , "uniteeeeeeeeeeee222222");

    const num = parseFloat(value);
    if (isNaN(num)) return '';

    console.log("unit", unit);
    // Convert only if unit is IN
    return unit === 'in' ? num * 2.54 : num;
  };
  // ---- Pricing Logic ----
  function calcFlatBeddingAddonTopSheet(widthIn, lengthIn, perMeterPrice, colorPrice) {
    /*if (!widthIn || !lengthIn) return 0.0;
    const F = inchesToCm(widthIn);
    const G = inchesToCm(lengthIn);
    
    const minDim = Math.min(F, G);
    const checkWidth1 = minDim + 2 * E;
    if (checkWidth1 > D) return 0;
    const maxDim = Math.max(F, G);
    const checkWidth2 = maxDim + 2 * E;
    const fabricInMeter = checkWidth2 / 100;
    const price = fabricInMeter * (variantPrice || 0);
    return price.toFixed(2);*/

    //const result = { price: 0, message: '' };
    const result = { price: 0, error_message: '', warning_message: '' };

    //if (!widthIn || !lengthIn) return 0.00;
    if (!widthIn || !lengthIn) {
      return result;
    }

    const F = inchesToCm(widthIn);
    const G = inchesToCm(lengthIn);

    const minDim = Math.min(F, G);
    const checkWidth1 = minDim + (2 * E);
    //if (checkWidth1 > D) return 0;
    if (checkWidth1 > D) {
      result.error_message = CUSTOM_MESSAGES.width_too_large;
      return result;
    }

    const maxDim = Math.max(F, G);
    const checkWidth2 = maxDim + (2 * E);
    if (checkWidth2 > D) {
      const fabricInMeter = checkWidth2 / 100;
      const price = (fabricInMeter * perMeterPrice) * colorPrice;
      result.price = price.toFixed(2);
      return result;
      //return price.toFixed(2);
    } else {
      const fabricInMeter = checkWidth1 / 100;
      const price = (fabricInMeter * perMeterPrice) * colorPrice;
      result.price = price.toFixed(2);
      return result;
      //return price.toFixed(2);
    }
  }

  // ---- Pricing Logic ----
  function calcFlatBeddingAddonFittedSheet(widthIn, lengthIn, depthIn, perMeterPrice, colorPrice) {
    /*if (!widthIn || !lengthIn || !depthIn) return 0.00;

    const F = inchesToCm(widthIn);
    const G = inchesToCm(lengthIn);
    const H = inchesToCm(depthIn);

    // Step 1: minimum dimension + 2*E
    let resultMin = Math.min(F, G) + (2 * E);
    if (resultMin > D) return 0; // stop if width exceeds max roll

    // Add 2*H
    resultMin = resultMin + (2 * H);
    if (resultMin > D) {
      // Step 2: maximum dimension path
      let resultMax = Math.max(F, G) + (4 * E) + (4 * H); 
      const fabricInMeter = resultMax / 100;
      const price = fabricInMeter * variantPrice;
      return price.toFixed(2);
    } else {
      // Step 3: max dimension path with normal additions
      let resultMax = Math.max(F, G) + (2 * E) + (2 * H);
      const fabricInMeter = resultMax / 100;
      const price = fabricInMeter * perMeterPrice;
      return price.toFixed(2);
    }*/

    //const result = { price: 0, message: '' };
    const result = { price: 0, error_message: '', warning_message: '' };

    /*if (!widthIn || !lengthIn || !depthIn) {
      result.price = 0.00;
      result.message = '';
      return result;
    }*/

    const F = inchesToCm(widthIn);
    const G = inchesToCm(lengthIn);
    const H = inchesToCm(depthIn);

    // Step 1: minimum dimension + 2*E
    let resultMin = Math.min(F, G) + (2 * E);

    // Test One
    if (resultMin > D) {
      result.error_message = CUSTOM_MESSAGES.width_too_large;
      return result;
    }

    if (!widthIn || !lengthIn || !depthIn) {
      return result;
    }

    // Add 2*H + 2*factor
    resultMin = resultMin + M + (2 * H);
    //Test 2
    if (resultMin > D) {
      // Step 2: maximum dimension path
      let resultMax = Math.max(F, G) + (2 * E);
      if (resultMax <= D) {
        let resultAlternate = Math.min(F, G) + (2 * E) + (2 * M) + (4 * H);
        const fabricInMeter = resultAlternate / 100;
        const price = (fabricInMeter * perMeterPrice) * colorPrice;
        result.price = price.toFixed(2);
        result.warning_message = CUSTOM_MESSAGES.multi_piece;
        return result;
      } else {
        let resultAlternate = Math.max(F, G) + (2 * E) + (2 * M) + (4 * H);
        const fabricInMeter = resultAlternate / 100;
        const price = (fabricInMeter * perMeterPrice) * colorPrice;
        result.price = price.toFixed(2);
        result.warning_message = CUSTOM_MESSAGES.multi_piece;
        return result;
      }
    } else {
      // Step 3: max dimension path with normal additions
      let resultMax = Math.max(F, G) + M + (2 * E) + (2 * H);
      if (resultMax <= D) {
        const fabricInMeter = resultMin / 100;
        const price = (fabricInMeter * perMeterPrice) * colorPrice;
        result.price = price.toFixed(2);
        return result;
      } else {
        const fabricInMeter = resultMax / 100;
        const price = (fabricInMeter * perMeterPrice) * colorPrice;
        result.price = price.toFixed(2);
        return result;
      }
    }
  }

  // ---- Round Bedding Addon Top Sheet Pricing Logic ----
  function calcRoundBeddingAddonTopSheet(widthIn, lengthIn, perMeterPrice, colorPrice) {
    //const result = { price: 0, message: '' };
    const result = { price: 0, error_message: '', warning_message: '' };

    if (!widthIn || !lengthIn) {
      return result;
    }

    const F = inchesToCm(widthIn);
    const G = inchesToCm(lengthIn);

    const minDim = Math.min(F, G);
    const checkWidth1 = minDim + (2 * E);

    if (checkWidth1 > D) {
      result.error_message = CUSTOM_MESSAGES.width_too_large;
      return result;
    }

    const maxDim = Math.max(F, G);
    const checkWidth2 = maxDim + (2 * E);
    if (checkWidth2 > D) {
      const fabricInMeter = checkWidth2 / 100;
      const price = (fabricInMeter * perMeterPrice) * colorPrice;
      result.price = price.toFixed(2);
      return result;
    } else {
      const fabricInMeter = checkWidth1 / 100;
      const price = (fabricInMeter * perMeterPrice) * colorPrice;
      result.price = price.toFixed(2);
      return result;
    }
  }

  // ---- Round Bedding Addon Fitted Sheet Pricing Logic ----
  function calcRoundBeddingAddonFittedSheet(diameterIn, depthIn, perMeterPrice, colorPrice) {
    //const result = { price: 0, message: '' };
    const result = { price: 0, error_message: '', warning_message: '' };

    /*if (!diameterIn || !depthIn) {
      result.price = 0.00;
      result.message = '';
      return result;
    }*/

    const F = inchesToCm(diameterIn);
    const H = inchesToCm(depthIn);

    // Step 1: minimum dimension + 2*E
    let resultMin = F + (2 * E);

    // Test One
    if (resultMin > D) {
      result.message = CUSTOM_MESSAGES.width_too_large;
      return result;
    }

    if (!diameterIn || !depthIn) {
      return result;
    }

    // Add 2*H + 2*factor
    resultMin = resultMin + M + (3 * H);
    const fabricInMeter = resultMin / 100;
    const price = ((fabricInMeter * perMeterPrice) * colorPrice) * N;
    result.price = price.toFixed(2);
    return result;
  }

  function calculatePriceAddonTopSheet(width, length, productType, qty = 1) {
    if (productType === 'Flat Bedding') {
      return calcFlatBeddingAddonTopSheet(width, length, variantPrice || 0);
    }
    return 0;
  }

  // ----------------------------
  // 🟩 FORMULA: Custom Shape BEDDING (Top Sheet)
  // ----------------------------
  function calcFlatBeddingAddonTopSheet(widthIn, lengthIn, perMeterPrice, colorPrice) {
    const result = { price: 0, error_message: '', warning_message: '' };

    if (!widthIn || !lengthIn) {
      return result;
    }

    const F = inchesToCm(widthIn);
    const G = inchesToCm(lengthIn);

    const minDim = Math.min(F, G);
    const checkWidth1 = minDim + (2 * E);
    if (checkWidth1 > D) {
      result.error_message = CUSTOM_MESSAGES.width_too_large;
      return result;
    }

    const maxDim = Math.max(F, G);
    const checkWidth2 = maxDim + (2 * E);
    if (checkWidth2 > D) {
      const fabricInMeter = checkWidth2 / 100;
      const price = (fabricInMeter * perMeterPrice) * colorPrice;
      result.price = price.toFixed(2);
      return result;
    } else {
      const fabricInMeter = checkWidth1 / 100;
      const price = (fabricInMeter * perMeterPrice) * colorPrice;
      result.price = price.toFixed(2);
      return result;
    }
  }

  // ----------------------------
  // 🟩 FORMULA: Custom Shape BEDDING (Fitted Sheet)
  // ----------------------------
  function calcCustomShapeBeddingAddonFittedSheet(widthIn, lengthIn, depthIn, perMeterPrice, colorPrice) {
    //const result = { price: 0, message: '' };
    const result = { price: 0, error_message: '', warning_message: '' };

    if (!widthIn || !lengthIn) {
      return result;
    }

    const F = inchesToCm(widthIn);
    const G = inchesToCm(lengthIn);
    const H = inchesToCm(depthIn);

    const minDim = Math.min(F, G);
    const checkWidth1 = minDim + (2 * E);

    if (checkWidth1 > D) {
      result.error_message = CUSTOM_MESSAGES.width_too_large;
      return result;
    }

    const maxDim = Math.max(F, G);
    const checkWidth2 = maxDim + (3 * H) + M + (2 * E);
    if (checkWidth2 > D) {
      const fabricInMeter = checkWidth2 / 100;
      const price = ((fabricInMeter * perMeterPrice) * colorPrice) * N;
      result.price = price.toFixed(2);
      return result;
    } else {
      const fabricInMeter = (checkWidth1 + (3 * H) + M) / 100;
      const price = ((fabricInMeter * perMeterPrice) * colorPrice) * N;
      result.price = price.toFixed(2);
      return result;
    }
  }

  function calculatePriceAddonFittedSheet(width, length, depth, productType, qty = 1) {
    if (productType === 'Flat Bedding') {
      return calcFlatBeddingAddonFittedSheet(width, length, depth, variantPrice || 0);
    }
    return 0;
  }

  // ---- Getters ----
  const getQty = row => parseNum(getQtyEl(row)?.value, 1);
  const customAllFilled = row =>
    getCustomInputs(row).every(inp => String(inp.value || '').trim() !== '');
  const selectHasValue = row => {
    const sel = getSelect(row);
    return !!(sel && sel.value && sel.selectedIndex > -1);
  };

  // ---- Main Unit Price ----
  function getUnitPrice(row) {
    //console.log(row)
    const customInputs = getCustomInputs(row);
    const firstInput = customInputs[0];
    const section = firstInput?.dataset?.section || '';
    //console.log(section)
    const colorPrice = getSelectedColorPrice(section);
    // If manual inputs used
    if (customInputs.length > 0 && customAllFilled(row)) {
      const width = inchesToCm(parseNum(row.querySelector('.addon-bedsheet-width')?.value, 0));
      const length = inchesToCm(parseNum(row.querySelector('.addon-bedsheet-length')?.value, 0));
      const depth = inchesToCm(parseNum(row.querySelector('.addon-bedsheet-depth')?.value, 0));
      const diameter = inchesToCm(parseNum(row.querySelector('.addon-bedsheet-diameter')?.value, 0));
      /*const productType =
        row.querySelector('.addon-bedsheet-width')?.dataset.productType || '';
      const section =
        row.querySelector('.addon-bedsheet-width')?.dataset.section || '';*/
        // --- Get productType & section from the FIRST input field ---
      //const firstInput = customInputs[0];
      const productType = firstInput?.dataset?.productType || '';
      //const section = firstInput?.dataset?.section || '';
      const qty = getQty(row);
      //console.log(section+'---custom')
      let priceObj;
      if (section === 'Top-sheet') {
        //const colorPrice = getSelectedColorPrice('Top-sheet');
        if (productType == 'Flat Bedding') {
          priceObj = calcFlatBeddingAddonTopSheet(width, length, variantPrice || 0, colorPrice);
        } else if (productType == 'Round Bedding') {
          priceObj = calcRoundBeddingAddonTopSheet(width, length, variantPrice || 0, colorPrice);
        } else if (productType == 'RV-boat fitted sheet') {
          priceObj = calcCustomShapeBeddingAddonTopSheet(width, length, variantPrice || 0, colorPrice);
        }
      } else if (section === 'Fitted-sheet') {
        //const colorPrice = getSelectedColorPrice('Fitted-sheet');
        if (productType == 'Flat Bedding') {
          priceObj = calcFlatBeddingAddonFittedSheet(width, length, depth, variantPrice || 0, colorPrice);
        } else if (productType == 'Round Bedding') {
          priceObj = calcRoundBeddingAddonFittedSheet(diameter, depth, variantPrice || 0, colorPrice);
        } else if (productType == 'Duvet Cover') {
          priceObj = calcFlatBeddingAddonFittedSheet(width, length, depth, variantPrice || 0, colorPrice);
        } else if (productType == 'RV-boat fitted sheet') {
          priceObj = calcCustomShapeBeddingAddonFittedSheet(width, length, depth, variantPrice || 0, colorPrice);
        }
        //priceObj = calculatePriceAddonFittedSheet(width, length, depth, productType, qty);
      }

      console.log(priceObj);

      const manualWrapper = row.querySelector('.manual-size-wrapper'); // message goes here
      // --- Show or update error message in UI ---
      let msgEl = manualWrapper.querySelector('.addon-error-message');
      /*if (!msgEl) {
        msgEl = document.createElement('div');
        msgEl.className = 'addon-error-message';
        msgEl.style.color = 'red';
        msgEl.style.marginTop = '5px';
        manualWrapper.prepend(msgEl);
      }*/
      if (!msgEl) {
        msgEl = document.createElement('div');
        msgEl.className = 'addon-error-message';
        msgEl.style.marginTop = '5px';
        manualWrapper.prepend(msgEl);
      }

      // Reset text and color first
      msgEl.textContent = '';
      msgEl.style.color = '';

      // --- Handle error/warning separately ---
      if (priceObj.error_message) {
        msgEl.textContent = priceObj.error_message;
        msgEl.style.color = 'red';
        msgEl.dataset.type = 'error';
      } else if (priceObj.warning_message) {
        showWarningPopup(priceObj.warning_message);
        // msgEl.textContent = priceObj.warning_message;
        // msgEl.style.color = 'orange';
        // msgEl.dataset.type = 'warning';
      } else {
        msgEl.removeAttribute('data-type');
      }

      //msgEl.textContent = priceObj.message || '';
      return parseNum(priceObj.price, 0);
    }

    

    // Otherwise dropdown
    const sel = getSelect(row);
    const opt = sel?.options?.[sel.selectedIndex];
    const price = parseNum(opt?.dataset?.price, 0);
    return (price * colorPrice) * conversionRate; // multiply with conversion rate
    //return parseNum(opt?.dataset?.price, 0);
  }

  // ---- UI Update ----
  function updateTotal(row) {
    const out = row?.querySelector(DISPLAY);
    if (!out) return;
    const total = (getUnitPrice(row) * getQty(row)).toFixed(2);
    out.textContent = total;
  }

  function applyBtnState(row) {
    const customInputs = getCustomInputs(row);
    const btn = getBtn(row);
    if (!btn) return;
    const qtyOk = getQty(row) > 0;
    const valid = qtyOk && (selectHasValue(row) || (customInputs.length > 0 && customAllFilled(row)));

    // Disable also if there’s any visible addon error
    //const hasError = !!row.querySelector('.addon-error-message')?.textContent.trim();
    const msgEl = row.querySelector('.addon-error-message');
    const msgType = msgEl?.dataset?.type || '';
    const hasError = msgType === 'error'; // disable only for error

    btn.disabled = !valid || hasError;
    btn.classList.toggle('is-disabled', !valid);
  }

  function getSelectedColorData(section) {
    // Find checked color input in the given section
    const selectedColor = document.querySelector(
      `.color-radio[data-section="${section}"]:checked`
    );

    if (!selectedColor) {
      // Section may not have color options or none selected
      return null;
    }

    return {
      colorValue: selectedColor.value, // e.g. "#86a8cb"
    };
  }

  // ---- Event: Dropdown Change ----
  document.addEventListener('change', e => {
    const row = getRow(e.target);
    if (!row) return;

    if (e.target.matches(SELECT)) {
      // Clear custom inputs when dropdown used
      getCustomInputs(row).forEach(inp => (inp.value = ''));
      updateTotal(row);
      applyBtnState(row);
      return;
    }

    if (e.target.matches(QTY)) {
      updateTotal(row);
      applyBtnState(row);
      return;
    }

    if (e.target.matches(CUSTOM_INPUTS)) {
      // Clear dropdown if manual input entered
      const sel = getSelect(row);
      if (sel) sel.value = '';
      updateTotal(row);
      applyBtnState(row);
    }
  });

  // ---- Event: Input (manual typing) ----
  document.addEventListener('input', e => {
    const row = getRow(e.target);
    if (!row) return;

    const customInputs = getCustomInputs(row);

    if (customInputs.length > 0 && (e.target.matches(CUSTOM_INPUTS) || e.target.matches(QTY))) {
      const sel = getSelect(row);
      if (sel && customAllFilled(row)) sel.value = '';
      updateTotal(row);
      applyBtnState(row);
    }
  });

  // ---- Add to Cart ----
  async function addAddonToCart({ id, quantity, properties }) {
    console.log(properties, "properties");
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: Number(id), quantity: Number(quantity), properties }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.description || `Add failed (${res.status})`);
    }
    return res.json();
  }

  document.addEventListener('click', async e => {
    const btn = e.target.closest(BTN);
    if (!btn) return;
    const row = getRow(btn);
    if (!row) return;

    if (btn.disabled) return;

    const qty = getQty(row);
    const price = getUnitPrice(row).toFixed(2);
    const sel = getSelect(row);

    let size = '';
    let section = '';

    if (sel && sel.value) {
      // Use select dropdown value if available
      size = sel.value;
      section = sel.dataset.section || '';
    } else {
      // Otherwise, use all custom input values
      const customInputs = Array.from(row.querySelectorAll('.addon-input'));
      const sizeParts = [];

      customInputs.forEach(input => {
        const val = input.value?.trim();
        if (val) {
          let suffix = '';
          if (input.classList.contains('addon-bedsheet-width')) suffix = 'W';
          else if (input.classList.contains('addon-bedsheet-length')) suffix = 'L';
          else if (input.classList.contains('addon-bedsheet-depth')) suffix = 'D';
          else if (input.classList.contains('addon-bedsheet-diameter')) suffix = 'D';

          sizeParts.push(`${val} (${suffix})`);

          // Get section from the first input
          if (!section) section = input.dataset.section || '';
        }
      });

      size = sizeParts.join('x'); // e.g. 100 (W)x100 (L)x200 (D)
    }

    const colorData = getSelectedColorData(section);

    const props = {
      //'Addon Type': 'Bedsheet',
      Size: size,
      Section: section,
      '_price-total-sheet': price,
    };

    // ✅ Add color property only if colorData exists
    if (colorData) {
      props['Color'] = colorData.colorValue;
    }

    btn.disabled = true;
    btn.classList.add('is-disabled');
    try {
      console.log(properties, "properties111111");
      await addAddonToCart({ id: STATIC_VARIANT_ID, quantity: qty, properties: props });
      window.location.href = '/cart';
    } catch (err) {
      console.error(err);
      alert(err.message || 'Unable to add addon');
    } finally {
      btn.disabled = false;
      btn.classList.remove('is-disabled');
    }
  });


  // ---- Initialize ----
  document.querySelectorAll(ROW).forEach(row => {
    updateTotal(row);
    applyBtnState(row);
  });
}


function restoreCustomDimensions() {
  console.log("fffffffffffff");
  console.log(window.meta?.variant?.id, "variantID");
  const productId = window.meta?.product?.id || window.CUSTOM_PRODUCT_ID;
  if (!productId) return;

  const storageKey = `custom_dimensions_${productId}`;
  const storedData = JSON.parse(localStorage.getItem(storageKey) || "{}");
  if (!storedData || Object.keys(storedData).length === 0) return;

  document.querySelectorAll('.size-filled-custom').forEach(wrapper => {
    let section = wrapper.dataset.section;
    if (!section) {
      const firstInput = wrapper.querySelector('.manual-size-input, .size-dropdown');
      if (firstInput) section = firstInput.dataset.section;
    }
    if (!section || !storedData[section]) return;

    const sectionData = storedData[section];

    const widthEl = wrapper.querySelector('.manual-width');
    const lengthEl = wrapper.querySelector('.manual-length');
    const depthEl = wrapper.querySelector('.manual-depth');
    const diameterEl = wrapper.querySelector('.manual-diameter');

    if (widthEl && sectionData.width) widthEl.value = sectionData.width;
    if (lengthEl && sectionData.length) lengthEl.value = sectionData.length;
    if (depthEl && sectionData.depth) depthEl.value = sectionData.depth;
    if (diameterEl && sectionData.diameter) diameterEl.value = sectionData.diameter;

    const dropdown = wrapper.querySelector('.size-dropdown');
    if (dropdown && sectionData.dropdownValue) dropdown.value = sectionData.dropdownValue;

    [widthEl, lengthEl, depthEl, diameterEl].forEach(input => {
      if (input) input.dispatchEvent(new Event('input', { bubbles: true }));
    });
  });
  initaddonUI();
}



function topshapecustom() {

  const ROOT = document.querySelector('.custom-shape-selector-custom');
  if (!ROOT) return;

  const PER_INCH_PRICE = 2; // static rate for shape dimensions
  const PRICE_OUT = document.querySelector('#calculated-price'); // Display total price

  // Quantity (Dawn-like input fields)
  const QTY_INPUT = document.querySelector('.price-quantity-wrap .quantity__input');
  const QTY_BTNS = document.querySelectorAll('.price-quantity-wrap .quantity__button');
  //return false;
  const PRODUCT_SCOPE = ROOT.closest('.product-form') || document;
  // ✅ Find the product form via parent .product-form (no hard-coded ID)
  const FORM = ROOT.closest('.product-form')?.querySelector('form') || document.querySelector('.product-form form');
  if (!FORM) return;

  // Submit button + label
  const SUBMIT_BTN = PRODUCT_SCOPE.querySelector('.product-form__submit');
  const SUBMIT_LABEL = SUBMIT_BTN?.querySelector('span');

  // Where we’ll mount all auto-created hidden inputs
  const PROPS_MOUNT_ID = 'shape-props-mount';
  function ensurePropsMount() {
    let m = FORM?.querySelector('#' + PROPS_MOUNT_ID);
    if (!m && FORM) {
      m = document.createElement('div');
      m.id = PROPS_MOUNT_ID;
      m.style.display = 'none';
      FORM.appendChild(m);
    }
    return m || null;
  }
  const PROPS_MOUNT = ensurePropsMount();

  // Helper functions
  function getSideInputs() {
    return Array.from(ROOT.querySelectorAll('#dynamic-inputs input'));
  }

  function getQty() {
    const q = parseFloat(QTY_INPUT?.value);
    return Number.isFinite(q) && q > 0 ? q : 1;
  }

  function allPositive(nums) {
    return nums.length > 0 && nums.every(v => Number.isFinite(v) && v > 0);
  }

  function product(nums) {
    return nums.reduce((a, v) => a * v, 1);
  }

  function fmt(n) {
    return (Math.round(n * 100) / 100).toFixed(2);
  }

  // Selected shape helpers
  function getActiveShape() {
    const shapesWrap = ROOT.querySelector('.shapes');
    return shapesWrap?.querySelector('.shape.active') || null;
  }

  // Hidden property helpers
  function setProp(key, value) {
    console.log(value, "ssssssssss");
    if (!PROPS_MOUNT) return;
    const name = `properties[${key}]`;
    const esc = window.CSS?.escape ? CSS.escape(name) : name.replace(/"/g, '\\"');
    let inp = PROPS_MOUNT.querySelector(`input[name="${esc}"]`);
    if (!inp) {
      inp = document.createElement('input');
      inp.type = 'hidden';
      inp.name = name;
      PROPS_MOUNT.appendChild(inp);
    }
    inp.value = String(value ?? '');
  }

  function removeProp(key) {
    if (!PROPS_MOUNT) return;
    const name = `properties[${key}]`;
    const esc = window.CSS?.escape ? CSS.escape(name) : name.replace(/"/g, '\\"');
    PROPS_MOUNT.querySelectorAll(`input[name="${esc}"]`).forEach(n => n.remove());
  }

  // Add total price with color price
  function updateTotalPrice() {
    const values = getSideInputs().map(i => parseFloat(i.value));
    const sidesOk = allPositive(values);
    const qty = getQty();
    const colorPrice = getSelectedColorPrice(); // Get total color price
    const base = sidesOk ? product(values) : 0;
    const unit = base * PER_INCH_PRICE;
    const total = (unit + colorPrice) * qty;
    if (PRICE_OUT) PRICE_OUT.textContent = fmt(total);
  }

  // Where your hidden inputs live (or just search the form)
  const PROPS_SCOPE = FORM; // or a mount node if you use one

  function clearShapeProps(scope = PROPS_SCOPE) {
    // Remove Shape*, Side*, and Shape Unit Price properties
    const selectors = [
      'input[name^="properties[Shape "]',
      'input[name^="properties[Side "]',
      'input[name="properties[_price-total-sheet]"]'
    ];
    scope.querySelectorAll(selectors.join(',')).forEach(n => n.remove());
  }

  // Button enable/disable
  function setBtn(enabled) {
    if (!SUBMIT_BTN) return;
    SUBMIT_BTN.disabled = !enabled;
    SUBMIT_BTN.classList.toggle('is-disabled', !enabled);
    SUBMIT_BTN.setAttribute('aria-disabled', enabled ? 'false' : 'true');
    if (SUBMIT_LABEL) SUBMIT_LABEL.textContent = enabled ? 'Add to cart' : theme.strings.selectAllFields;
  }

  // Core recalc + property sync
  function recalcAndSyncProps() {
    /*const values = getSideInputs().map(i => parseFloat(i.value));
    const qtyOk = getQty() > 0;
    const sidesOk = allPositive(values);

    const valid = sidesOk && qtyOk;*/

    // 1) Get area directly from the area-output field
    const areaField = document.querySelector('.area-output');
    const area = parseFloat(areaField?.value || 0);

    //const qtyOk = getQty() > 0;
    //const areaOk = Number.isFinite(area) && area > 0;

    //const valid = areaOk && qtyOk;

    // Update UI price (total = unit * qty)
    //const colorPrice = getSelectedColorPrice(); // Get total color price
    //const base = valid ? product(values) : 0;
    //const base = valid ? area : 0;

    //const unit = (base * PER_INCH_PRICE) + colorPrice; // single quantity price
    //const unit = base > 0 ? (base * PER_INCH_PRICE) + colorPrice : 0; // Add color price if base > 0
    //const total = unit * getQty();
    //if (PRICE_OUT) PRICE_OUT.textContent = fmt(total);

    // Sync properties only if we have a form/mount
    if (!PROPS_MOUNT) return;

    // 1) Selected shape properties (always keep them current)
    const active = getActiveShape();
    const shapeId = active?.dataset?.id || '';
    const shapeImg = active?.dataset?.image || '';
    //if (shapeId) setProp('Shape ID', shapeId); else removeProp('Shape ID');
    if (shapeImg) setProp('_shape_image', shapeImg); else removeProp('_shape_image');

    // 2) Per-side values (set when positive, otherwise remove)
    /*const sideLabels = ['a', 'b', 'c', 'd', 'e', 'f'];
    getSideInputs().forEach((inp, i) => {
      const key = `Side ${sideLabels[i] || String(i + 1)}`;
      const v = parseFloat(inp.value);
      if (Number.isFinite(v) && v > 0) setProp(key, `${v} in`);
      else removeProp(key);
    });*/

    // Sync per-side values with meaningful names
    getSideInputs().forEach((inp) => {
      // Get the label text for this input (Width, Height, Top, Bottom, etc.)
      const labelEl = inp.closest('.multi-shapes-wrap')?.querySelector('p');
      const rawLabel = labelEl ? labelEl.textContent.replace(/\(|\)/g, '').trim() : 'dimension';
      const key = `_shape_${rawLabel.toLowerCase().replace(/\s+/g, '_')}`;
      
      const v = parseFloat(inp.value);
      if (Number.isFinite(v) && v > 0) {
        setProp(key, `${v} in`);
      } else {
        removeProp(key);
      }
    });

    // 3) Unit price for one quantity
    //if (valid) setProp('_price-total-sheet', fmt(unit));
    //else removeProp('_price-total-sheet');

    // 4) Set Color properties dynamically
    const colorSections = document.querySelectorAll('.color-list');
    colorSections.forEach((section) => {
      const selectedColor = section.querySelector('.color-radio:checked');
      const sectionName = section.id.replace('color-list-', '').replace(/-/g, ' '); // Extract section name dynamically (preserve case, replace all hyphens)
      if (selectedColor) {
        setProp(`${sectionName} Color`, selectedColor.dataset.colorName || selectedColor.value);
        //setProp(`${sectionName} Color Price`, selectedColor.getAttribute('data-color-price'));
      } else {
        removeProp(`${sectionName} Color`);
        //removeProp(`${sectionName} Color Price`);
      }
    });

    // Button state
    updateSubmitStateNew();
  }

  // Wire: shape selection → toggle active, clear old props, then recalc/sync
  ROOT.addEventListener('click', function (e) {
    const shape = e.target.closest('.shape');
    if (!shape || !shape.parentElement?.closest('.shapes')) return;

    // clear previous shape props (sides + unit price + shape fields)
    clearShapeProps();

    // recompute next frame
    requestAnimationFrame(recalcAndSyncProps);
  });

  // Shape side input changes
  ROOT.addEventListener('input', function (e) {
    if (!e.target.closest('#dynamic-inputs')) return;
    requestAnimationFrame(recalcAndSyncProps);
  });

  // Color changes (radio buttons for all sections)
  const colorSections = document.querySelectorAll('.color-list');
  colorSections.forEach(section => {
    section.querySelectorAll('.color-radio').forEach(radio => {
      radio.addEventListener('change', () => {
        requestAnimationFrame(recalcAndSyncProps);
      });
    });
  });

  // Quantity changes
  //QTY_INPUT?.addEventListener('input', () => requestAnimationFrame(recalcAndSyncProps));
  //QTY_BTNS.forEach(btn => btn.addEventListener('click', () => requestAnimationFrame(recalcAndSyncProps)));


  // Add to Cart button click (clear inputs and disable button)
  SUBMIT_BTN.addEventListener('click', function(e) {
    // Prevent form submission to run custom actions first
    setTimeout(() => {
      // Clear shape inputs
      clearShapeProps();

       // 2️⃣ Reset all dropdowns (size-dropdown, addon-dropdown, etc.)
      document.querySelectorAll('select.size-dropdown').forEach(select => {
        select.selectedIndex = 0; // reset to first option
      });

      ROOT.querySelectorAll('#dynamic-inputs input').forEach(input => input.value = '');
      ROOT.querySelectorAll('.manual-size-input').forEach(input => input.value = '');
      ROOT.querySelectorAll('.price-for-this-group-custom span').forEach(span => {
        span.textContent = '0.00';
      });
      // Reset total calculated price
      const calculatedPrice = document.getElementById('calculated-price');
      if (calculatedPrice) calculatedPrice.textContent = '0.00';

      setBtn(false);
    }, 1000)
  });
  // Initial compute
  recalcAndSyncProps();


  }

  // Get selected color prices dynamically
  /*function getSelectedColorPrice() {
    let totalColorPrice = 0;

    // Loop through all color sections
    const colorSections = document.querySelectorAll('.color-list');
    colorSections.forEach(section => {
      const selectedColor = section.querySelector('.color-radio:checked');
      if (selectedColor) {
        totalColorPrice += parseFloat(selectedColor.getAttribute('data-color-price')) || 0;
      }
    });

    return totalColorPrice;
  }*/


document.addEventListener('DOMContentLoaded', function () {
  initaddonUI();
  restoreCustomDimensions();
  productBundle();
  topshapecustom();
  // Code of Product Page Formula
  updateSubmitStateNew();
  tableCloths();
});

// Code of Product Page Formula Start
// Get the element
const variantPriceEl = document.querySelector('.variant_price');

// Get the value as a number
const variantPrice = variantPriceEl ? parseFloat(variantPriceEl.textContent) / 100 : 0;

// Get Currency Code
const currencyCodeEl = document.querySelector('.currency_code');

// Get the value as a number
const currencyCode = currencyCodeEl ? currencyCodeEl.textContent : 'IN';

// Define default conversion rates relative to INR
const conversionRates = {
  IN: 1,       // INR
  US: 0.012,   // USD
  EU: 0.011,   // EUR
  GB: 0.009,   // GBP
  // add more countries as needed
};

// Set conversion rate for current country, fallback to INR (1)
const conversionRate = conversionRates[currencyCode] || 1;

//document.addEventListener('DOMContentLoaded', function() {
function productBundle() {
  // Common constants
  console.log(getValue('.non_usable_portion', 10), 'i am here')
  const D = getValue('.max_roll_width', 500); // Maximum Roll Width (cm)
  const E = getValue('.non_usable_portion', 10);  // Non Usable Portion (cm)
  const M = getValue('.underside_width', 22);
  const N = getValue('.factor', 2);

  // Convert inches to centimeters
  // const inchesToCm = (inch) => inch * 2.54;
  let inchesToCm = (value) => {
    console.log("valueeeee", value);
    const unit =
      document.querySelector('input[name="dimension_unit"]:checked')?.value || 'in';

    console.log(unit , "uniteeeeeeeeeeee");

    const num = parseFloat(value);
    if (isNaN(num)) return '';

    console.log("unit", unit);
    // Convert only if unit is IN
    return unit === 'in' ? num * 2.54 : num;
  };

  // Get the quantity input
  const qtyInput = document.querySelector('.quantity__input');
  const quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

  // Optional: element to display total
  const totalEl = document.querySelector('#calculated-price');

  // Helper function: get number from span text
  function parsePrice(span) {
    if (!span) return 0;
    return parseFloat(span.textContent.replace(/[^\d.]/g, '')) || 0;
  }

  // Update total on quantity change
  if (qtyInput) {
    qtyInput.addEventListener('input', function () {
      updateTotalPrice();
    });
  }

  // Optional: update total when clicking plus/minus buttons
  document.querySelectorAll('.quantity__button').forEach(btn => {
    btn.addEventListener('click', function () {
      setTimeout(() => {
        updateTotalPrice();
      }, 50); // small delay to let input update
    });
  });

  // Function to sum all group prices and update total
  function updateTotalPrice() {
    const quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

    const priceSpans = document.querySelectorAll('.custom-color-wrap .price-for-this-group-custom span');
    let sum = 0;

    priceSpans.forEach(span => {
      console.log("priceSpans", parsePrice(span));
      sum += parsePrice(span);
    });

    const total = (sum * quantity).toFixed(2);

    // Update visible total
    if (totalEl) totalEl.textContent = total;

    // Update hidden Shopify property for total price
    const hiddenTotal = document.querySelector('#_price-total-sheet');
    if (hiddenTotal) hiddenTotal.value = sum.toFixed(2);

    // After updating total, check submit button state
    updateSubmitStateNew();
  }

  // ----------------------------
  // 🟩 FORMULA: FLAT BEDDING (Top Sheet)
  // ----------------------------
  function calcFlatBeddingTopSheet(widthIn, lengthIn, perMeterPrice, colorPrice) {
    //const result = { price: 0, message: '' };
    const result = { price: 0, error_message: '', warning_message: '' };

    //if (!widthIn || !lengthIn) return 0.00;
    if (!widthIn || !lengthIn) {
      return result;
    }

    const F = inchesToCm(widthIn);
    const G = inchesToCm(lengthIn);

    const minDim = Math.min(F, G);
    const checkWidth1 = minDim + (2 * E);
    //if (checkWidth1 > D) return 0;
    if (checkWidth1 > D) {
      result.error_message = CUSTOM_MESSAGES.width_too_large;
      return result;
    }

    const maxDim = Math.max(F, G);
    const checkWidth2 = maxDim + (2 * E);
    if (checkWidth2 > D) {
      const fabricInMeter = checkWidth2 / 100;
      const price = (fabricInMeter * perMeterPrice) * colorPrice;
      result.price = price.toFixed(2);
      return result;
      //return price.toFixed(2);
    } else {
      const fabricInMeter = checkWidth1 / 100;
      const price = (fabricInMeter * perMeterPrice) * colorPrice;
      result.price = price.toFixed(2);
      return result;
      //return price.toFixed(2);
    }
  }

  // ----------------------------
  // 🟩 FORMULA: FLAT BEDDING (Fitted Sheet)
  // ----------------------------
  function calcFlatBeddingFittedSheet(widthIn, lengthIn, depthIn, perMeterPrice, colorPrice) {
    //const result = { price: 0, message: '' };
    const result = { price: 0, error_message: '', warning_message: '' };

    /*if (!widthIn || !lengthIn || !depthIn) {
      result.price = 0.00;
      result.message = '';
      return result;
    }*/

    const F = inchesToCm(widthIn);
    const G = inchesToCm(lengthIn);
    const H = inchesToCm(depthIn);

    // Step 1: minimum dimension + 2*E
    let resultMin = Math.min(F, G) + (2 * E);

    //if (resultMin > D) return 0; // stop if width exceeds max roll
    // Test One
    if (resultMin > D) {
      result.error_message = CUSTOM_MESSAGES.width_too_large;
      return result;
    }

    if (!widthIn || !lengthIn || !depthIn) {
      return result;
    }

    // Add 2*H + 2*factor
    resultMin = resultMin + M + (2 * H);
    //Test 2
    if (resultMin > D) {
      // Step 2: maximum dimension path
      let resultMax = Math.max(F, G) + (2 * E);
      if (resultMax <= D) {
        let resultAlternate = Math.min(F, G) + (2 * E) + (2 * M) + (4 * H);
        const fabricInMeter = resultAlternate / 100;
        const price = (fabricInMeter * perMeterPrice) * colorPrice;
        result.price = price.toFixed(2);
        result.warning_message = CUSTOM_MESSAGES.multi_piece;
        return result;
      } else {
        let resultAlternate = Math.max(F, G) + (2 * E) + (2 * M) + (4 * H);
        const fabricInMeter = resultAlternate / 100;
        const price = (fabricInMeter * perMeterPrice) * colorPrice;
        result.price = price.toFixed(2);
        result.warning_message = CUSTOM_MESSAGES.multi_piece;
        return result;
      }
      //const fabricInMeter = resultMax / 100;
      //const price = fabricInMeter * perMeterPrice;
      //return price.toFixed(2);
    } else {
      // Step 3: max dimension path with normal additions
      let resultMax = Math.max(F, G) + M + (2 * E) + (2 * H);
      if (resultMax <= D) {
        const fabricInMeter = resultMin / 100;
        const price = (fabricInMeter * perMeterPrice) * colorPrice;
        result.price = price.toFixed(2);
        return result;
        //return price.toFixed(2);
      } else {
        const fabricInMeter = resultMax / 100;
        const price = (fabricInMeter * perMeterPrice) * colorPrice;
        result.price = price.toFixed(2);
        return result;
        //return price.toFixed(2);
      }
      
    }
  }

  // ----------------------------
  // 🟩 FORMULA: Round BEDDING (Top Sheet)
  // ----------------------------
  function calcRoundBeddingTopSheet(widthIn, lengthIn, perMeterPrice, colorPrice) {
    //const result = { price: 0, message: '' };
    const result = { price: 0, error_message: '', warning_message: '' };

    if (!widthIn || !lengthIn) {
      return result;
    }

    const F = inchesToCm(widthIn);
    const G = inchesToCm(lengthIn);

    const minDim = Math.min(F, G);
    const checkWidth1 = minDim + (2 * E);

    if (checkWidth1 > D) {
      result.error_message = CUSTOM_MESSAGES.width_too_large;
      return result;
    }

    const maxDim = Math.max(F, G);
    const checkWidth2 = maxDim + (2 * E);
    if (checkWidth2 > D) {
      const fabricInMeter = checkWidth2 / 100;
      const price = (fabricInMeter * perMeterPrice) * colorPrice;
      result.price = price.toFixed(2);
      return result;
    } else {
      const fabricInMeter = checkWidth1 / 100;
      const price = (fabricInMeter * perMeterPrice) * colorPrice;
      result.price = price.toFixed(2);
      return result;
    }
  }

  // ----------------------------
  // 🟩 FORMULA: Round BEDDING (Fitted Sheet)
  // ----------------------------
  function calcRoundBeddingFittedSheet(diameterIn, depthIn, perMeterPrice, colorPrice) {
    //const result = { price: 0, message: '' };
    const result = { price: 0, error_message: '', warning_message: '' };

    /*if (!diameterIn || !depthIn) {
      result.price = 0.00;
      result.message = '';
      return result;
    }*/

    const F = inchesToCm(diameterIn);
    const H = inchesToCm(depthIn);

    // Step 1: minimum dimension + 2*E
    let resultMin = F + (2 * E);

    // Test One
    if (resultMin > D) {
      result.error_message = CUSTOM_MESSAGES.width_too_large;
      return result;
    }

    if (!diameterIn || !depthIn) {
      return result;
    }

    // Add 2*H + 2*factor
    resultMin = resultMin + M + (3 * H);
    const fabricInMeter = resultMin / 100;
    const price = ((fabricInMeter * perMeterPrice) * colorPrice) * N;
    result.price = price.toFixed(2);
    return result;
  }

  // ----------------------------
  // 🟩 FORMULA: Round BEDDING (Top Sheet)
  // ----------------------------
  function calcDuvetCover(widthIn, lengthIn, perMeterPrice, totalColorMultiplier) {
    console.log(widthIn, lengthIn, perMeterPrice, totalColorMultiplier, "testsssss");
    //const result = { price: 0, message: '' };
    const result = { price: 0, error_message: '', warning_message: '' };

    if (!widthIn || !lengthIn) {
      return result;
    }

    const F = inchesToCm(widthIn);
    const G = inchesToCm(lengthIn);

    const minDim = Math.min(F, G);
    if (minDim + (2 * E) > D) {
      result.error_message = CUSTOM_MESSAGES.width_too_large;
      return result;
    }

    const maxDim = Math.max(F, G);
    const fabricInMeter = (maxDim + (2 * E)) / 100;

    console.log(fabricInMeter+'==='+totalColorMultiplier)
    let price = fabricInMeter;

    // If there are color selections, multiply total price accordingly
    if (totalColorMultiplier > 0) {
      price = price * totalColorMultiplier;
    }

    result.price = (Math.ceil(price * 10) / 10).toFixed(2);
    return result;
  }

  // ----------------------------
  // 🟩 FORMULA: Round BEDDING (Fitted Sheet)
  // ----------------------------
  function calcCustomShapeBeddingFittedSheet(widthIn, lengthIn, depthIn, perMeterPrice, colorPrice) {
    //const result = { price: 0, message: '' };
    const result = { price: 0, error_message: '', warning_message: '' };

    if (!widthIn || !lengthIn) {
      return result;
    }

    const F = inchesToCm(widthIn);
    const G = inchesToCm(lengthIn);
    const H = inchesToCm(depthIn);

    const minDim = Math.min(F, G);
    const checkWidth1 = minDim + (2 * E);

    if (checkWidth1 > D) {
      result.error_message = CUSTOM_MESSAGES.width_too_large;
      return result;
    }

    const maxDim = Math.max(F, G);
    const checkWidth2 = maxDim + (3 * H) + M + (2 * E);
    if (checkWidth2 > D) {
      const fabricInMeter = checkWidth2 / 100;
      const price = ((fabricInMeter * perMeterPrice) * colorPrice) * N;
      result.price = price.toFixed(2);
      return result;
    } else {
      const fabricInMeter = (checkWidth1 + (3 * H) + M) / 100;
      const price = ((fabricInMeter * perMeterPrice) * colorPrice) * N;
      result.price = price.toFixed(2);
      return result;
    }
  }

  // ----------------------------
  // 🟩 FORMULA: Table Cloth (Rectangular)
  // ----------------------------
  function calcRectangularTableCloth(widthIn, lengthIn, perMeterPrice) {
    const result = { price: 0, error_message: '', warning_message: '' };

    //if (!widthIn || !lengthIn) return 0.00;
    if (!widthIn || !lengthIn) {
      return result;
    }

    const F = inchesToCm(widthIn);
    const G = inchesToCm(lengthIn);

    const minDim = Math.min(F, G);
    const checkWidth1 = minDim + (2 * E);
    if (checkWidth1 > D) {
      result.error_message = CUSTOM_MESSAGES.width_too_large;
      return result;
    }

    const maxDim = Math.max(F, G);
    const checkWidth2 = maxDim + (2 * E);
    if (checkWidth2 > D) {
      const fabricInMeter = checkWidth2 / 100;
      const price = fabricInMeter * perMeterPrice;
      result.price = price.toFixed(2);
      return result;
    } else {
      const fabricInMeter = checkWidth1 / 100;
      const price = fabricInMeter * perMeterPrice;
      result.price = price.toFixed(2);
      return result;
    }
  }

  // ----------------------------
  // 🟩 FORMULA: Table Cloth (Round)
  // ----------------------------
  function calcRoundTableCloth(diameterIn, depthIn, perMeterPrice) {
    const result = { price: 0, error_message: '', warning_message: '' };

    const F = inchesToCm(diameterIn);
    const H = inchesToCm(depthIn);

    // Step 1: minimum dimension + 2*E
    let resultMin = F + (2 * E);

    // Test One
    if (resultMin > D) {
      result.error_message = CUSTOM_MESSAGES.width_too_large;
      return result;
    }

    if (!diameterIn || !depthIn) {
      return result;
    }

    resultMin = resultMin + M + (3 * H);
    const fabricInMeter = resultMin / 100;
    const price = (fabricInMeter * perMeterPrice) * N;
    result.price = price.toFixed(2);
    return result;
  }

  // ----------------------------
  // ⚡ EVENT BINDING
  // ----------------------------
  document.querySelectorAll('.manual-size-input').forEach(input => {
    input.addEventListener('input', function() {
      const wrapper = input.closest('.size-filled-custom');
      const manualWrapper = wrapper.querySelector('.manual-size-wrapper'); // message goes here
      const widthEl = wrapper.querySelector('.manual-width');
      const lengthEl = wrapper.querySelector('.manual-length');
      const depthEl = wrapper.querySelector('.manual-depth'); // new
      const diameterEl = wrapper.querySelector('.manual-diameter'); // new
      let priceEl = wrapper.querySelector('.price-for-this-group-custom span');
      
      const width = parseFloat(widthEl?.value) || 0;
      const length = parseFloat(lengthEl?.value) || 0;
      const depth = parseFloat(depthEl?.value) || 0; // new
      const diameter = parseFloat(diameterEl?.value) || 0;
      const productType = input.dataset.productType;
      const section = input.dataset.section;

      const productId = window.meta?.product?.id;

      const storageKey = `custom_dimensions_${productId}`;
      console.log(section, "section------------");
      console.log(productType, "productType------------");

      // Get existing data
      const existingData = JSON.parse(localStorage.getItem(storageKey) || "{}");

      let sectionData = {};

      // Section 1 → Width & Length only
      if (
        section === 'Top-sheet' ||
        section === 'Rectangular-Table-Cloth' ||
        section === 'Cover' ||
        section === 'Duvet-Cover' ||
        section === 'Duvet-Cover-One'
      ) {
        sectionData = {
          ...(width !== 0 && { width }),
          ...(length !== 0 && { length })
        };
      }

      // Section 2 → Diameter & Depth only
      if (section === 'Round-Table-Cloth' ) {
        sectionData = {
          ...(diameter !== 0 && { diameter }),
          ...(depth !== 0 && { depth })
        };
      }

      if(section === 'Fitted-sheet') {
        sectionData = {
          ...(width !== 0 && { width }),
          ...(length !== 0 && { length }),
          ...(depth !== 0 && { depth }),
          ...(diameter !== 0 && { diameter }),
        };
      }

      // if(productType != 'RV-boat fitted sheet') {
        // Merge under section key
        const updatedData = {
          ...existingData,
          [section]: {
            ...(existingData[section] || {}),
            ...sectionData
          }
        };

        // Save only if section has valid data
        if (Object.keys(sectionData).length > 0) {
          localStorage.setItem(storageKey, JSON.stringify(updatedData));
        }
      // }
        

      console.log(width, length, depth, diameter, "custom-------------------");
      

      //alert(section)

      // Clear dropdown if user types in manual input
      const dropdown = wrapper.querySelector('.size-dropdown');
      if (dropdown) {
        //const hiddenInput = wrapper.querySelector(`#hidden-size-${section}`);
        //if (hiddenInput) hiddenInput.value = '';
        dropdown.value = "";
      }

      // Update hidden input if exists
      const hiddenInput = wrapper.querySelector(`#hidden-size-${section}`);
      if (hiddenInput) {
        const sizeParts = [];
        if (width) sizeParts.push(`${width} (W)`);
        if (length) sizeParts.push(`${length} (L)`);
        if (diameter) sizeParts.push(`${diameter} (D)`);
        if (depth) sizeParts.push(`${depth} (D)`);
        hiddenInput.value = sizeParts.join('x'); // e.g., 10 (W)x89 (L)x4 (D)
      }

      // Message container
      let msgEl = manualWrapper.querySelector('.size-error-message');
      /*if (!msgEl) {
        msgEl = document.createElement('div');
        msgEl.className = 'size-error-message';
        msgEl.style.color = 'red';
        msgEl.style.marginBottom = '5px';
        manualWrapper.prepend(msgEl);
      }*/

      if (!msgEl) {
        msgEl = document.createElement('div');
        msgEl.className = 'size-error-message';
        msgEl.style.marginTop = '5px';
        manualWrapper.prepend(msgEl);
      }

      // Reset text and color first
      msgEl.textContent = '';
      msgEl.style.color = '';

      

      let perMeterPrice = variantPrice; // default
      let priceObj;
      if (section === 'Top-sheet') {
        const colorPrice = getSelectedColorPrice('Top-sheet');
        //const price = calculatePriceTopSheet(width, length, productType);
        if (productType == 'Flat Bedding') {
          priceObj = calcFlatBeddingTopSheet(width, length, perMeterPrice, colorPrice);
        } else if (productType == 'Round Bedding') {
          priceObj = calcRoundBeddingTopSheet(width, length, perMeterPrice, colorPrice);
        }
        //priceEl.textContent = price;
      } else if (section === 'Fitted-sheet') {
        const colorPrice = getSelectedColorPrice('Fitted-sheet');
        if (productType == 'Flat Bedding') {
          priceObj = calcFlatBeddingFittedSheet(width, length, depth, perMeterPrice, colorPrice);
        } else if (productType == 'Round Bedding') {
          priceObj = calcRoundBeddingFittedSheet(diameter, depth, perMeterPrice, colorPrice);
        } else if (productType == 'RV-boat fitted sheet') {
          priceObj = calcCustomShapeBeddingFittedSheet(width, length, depth, perMeterPrice, colorPrice);
        }
        //const price = calculatePriceFittedSheet(width, length, depth, productType);
        //priceEl.textContent = price;
      } else if (section === 'Duvet-Cover-One' || section === 'Duvet-Cover' || section === 'Cover') {
        if (productType == 'Duvet Cover') {
          // --- NEW: handle color price multipliers ---
          let totalColorMultiplier = 0;

          // Get all color lists inside this wrapper (can be multiple groups)
          const colorLists = document.querySelectorAll('.color-list');
          colorLists.forEach(list => {
            const selected = list.querySelector('input.color-radio:checked');
            if (selected) {
              const colorPrice = parseFloat(selected.dataset.colorPrice || '1.0');
              totalColorMultiplier += colorPrice;
            }
          });

          priceObj = calcDuvetCover(width, length, perMeterPrice, totalColorMultiplier);
        }
      } else if (section === 'Rectangular-Table-Cloth') {
        // Update hidden input if exists
        const hiddenInput = wrapper.querySelector(`#hidden-size-Table-cloth`);
        if (hiddenInput) {
          const sizeParts = [];
          if (width) sizeParts.push(`${width} (W)`);
          if (length) sizeParts.push(`${length} (L)`);
          hiddenInput.value = sizeParts.join('x'); // e.g., 10 (W)x89 (L)x4 (D)
        }
        if (productType == 'Table cloth') {
          priceObj = calcRectangularTableCloth(width, length, perMeterPrice);
        }

        priceEl = wrapper.querySelector('.price-for-this-group-custom.rectangular-fields span');
      } else if (section === 'Round-Table-Cloth') {
        // Update hidden input if exists
        const new_depth = 1;
        const hiddenInput = wrapper.querySelector(`#hidden-size-Table-cloth`);
        if (hiddenInput) {
          const sizeParts = [];
          if (diameter) sizeParts.push(`${diameter} (D)`);
          if (new_depth) sizeParts.push(`${new_depth} (D)`);
          hiddenInput.value = sizeParts.join('x'); // e.g., 10 (W)x89 (L)x4 (D)
        }
        console.log(diameter, "diameter");
        console.log(new_depth, "depth");
        if (productType == 'Table cloth') {
          priceObj = calcRoundTableCloth(diameter, new_depth, perMeterPrice);
        }
        priceEl = wrapper.querySelector('.price-for-this-group-custom.round-fields span');
      }

      if (priceObj) {
        priceEl.textContent = priceObj.price || 0;
        //msgEl.textContent = priceObj.message || '';
      }
      // --- Handle error/warning separately ---
      if (priceObj.error_message) {
        msgEl.textContent = priceObj.error_message;
        msgEl.style.color = 'red';
        msgEl.dataset.type = 'error';
      } else if (priceObj.warning_message) {
        showWarningPopup(priceObj.warning_message);
        // msgEl.textContent = priceObj.warning_message;
        // msgEl.style.color = 'orange';
        // msgEl.dataset.type = 'warning';
      } else {
        msgEl.removeAttribute('data-type');
      }
      // Initial calculation
      updateTotalPrice();
    });
  });

  // ----------------------------
  // 🧮 MASTER CALCULATOR
  // ----------------------------
  function calculatePriceTopSheet(widthIn, lengthIn, productType) {
    let perMeterPrice = variantPrice; // default

    switch (productType) {
      case 'Flat Bedding':
        return calcFlatBeddingTopSheet(widthIn, lengthIn, perMeterPrice);
      case 'Round Bedding':
        // For circular bedding, only width input is needed (as diameter)
        return calcRoundBeddingTopSheet(widthIn, lengthIn, perMeterPrice);
      default:
        return 0;
    }
  }

  // ----------------------------
  // 🧮 MASTER CALCULATOR
  // ----------------------------
  function calculatePriceFittedSheet(widthIn, lengthIn, depthIn, productType) {
    let perMeterPrice = variantPrice; // default

    switch (productType) {
      case 'Flat Bedding':
        return calcFlatBeddingFittedSheet(widthIn, lengthIn, depthIn, perMeterPrice);
      /*case 'Circular Bedding':
        // For circular bedding, only width input is needed (as diameter)
        return calcCircularBedding(widthIn || lengthIn, E, perMeterPrice);
      default:
        return 0;*/
    }
  }

  // Handle dropdown selection
  const sizeDropdowns = document.querySelectorAll('.size-dropdown');
  sizeDropdowns.forEach(dropdown => {
    dropdown.addEventListener('change', function () {
      const wrapper = dropdown.closest('.size-filled-custom');
      const section = dropdown.id.replace('size-dropdown-','');
      const colorPrice = getSelectedColorPrice(section);

      // ✅ Remove existing error message
      const msgEl = wrapper.querySelector('.size-error-message');
      if (msgEl) msgEl.remove();

      // Set hidden input value for form
      const hiddenInput = wrapper.querySelector(`#hidden-size-${section}`);
      if (hiddenInput) hiddenInput.value = dropdown.value;

      // Clear manual inputs
      console.log(dropdown.dataset.section, "dropdown");
      wrapper.querySelectorAll('.manual-size-input').forEach(input => input.value = '');
      const productId = window.meta?.product?.id;
      const storageKey = `custom_dimensions_${productId}`;
      const data = JSON.parse(localStorage.getItem(storageKey) || "{}");
      if (data[section]) {
        delete data[section]; // ✅ remove only this section

        // Save updated object back
        localStorage.setItem(storageKey, JSON.stringify(data));
      }
      if (Object.keys(data).length === 0) {
        localStorage.removeItem(storageKey);
      }

      

      // Set price from dropdown data-price
      const selectedOption = dropdown.options[dropdown.selectedIndex];

      const price = selectedOption ? (parseFloat(selectedOption.dataset.price)*colorPrice)*conversionRate || 0 : 0;

      const priceEl = wrapper.querySelector('.price-for-this-group-custom span');
      if (priceEl) priceEl.textContent = price.toFixed(2);

      // Update total
      updateTotalPrice();
    });
  });

  // ----------------------------
  // ⚡ COLOR CHANGE HANDLER
  // ----------------------------
  document.querySelectorAll('.color-radio').forEach(radio => {
    radio.addEventListener('change', function() {
      const section = this.dataset.section;

      // Get the selected color price for this section
      const colorPrice = parseFloat(this.dataset.colorPrice) || 1;
      const colorName = parseFloat(this.dataset.name) || '';

      console.log(`Color changed in section: ${section}, colorPrice: ${colorPrice}`);
      console.log(`Color changed in section: ${section}, colorName: ${colorName}`);

      // Optional: visually update active label
      document.querySelectorAll(`.color-list [data-section="${section}"] + label`).forEach(lbl => {
        lbl.classList.remove('color-label--active');
      });
      const label = document.querySelector(`label[for="${this.id}"]`);
      if (label) label.classList.add('color-label--active');

      // Recalculate prices for all inputs under this section
      /*document.querySelectorAll(`.manual-size-input[data-section="${section}"]`).forEach(input => {
        input.dispatchEvent(new Event('input')); // trigger recalculation
      });

      // Also recalc dropdown-based price if any
      const dropdown = document.querySelector(`.size-dropdown[data-section="${section}"]`);
      if (dropdown && dropdown.value) {
        dropdown.dispatchEvent(new Event('change'));
      }*/
      let dropdownUsed = false;
      document.querySelectorAll(`.size-dropdown[data-section="${section}"]`).forEach(dropdown => {
        if (dropdown.value) {
          dropdown.dispatchEvent(new Event('change', { bubbles: true }));
          dropdownUsed = true;
        }
      });

      // 2️⃣ If no dropdown selected, trigger manual input recalculation
      if (!dropdownUsed) {
        document.querySelectorAll(`.manual-size-input[data-section="${section}"]`).forEach(input => {
          input.dispatchEvent(new Event('input', { bubbles: true }));
        });
      }

      // ----------------------------
      // Recalculate addon rows for the same section
      // ----------------------------
      document.querySelectorAll(`.addon-block`).forEach(row => {
        const firstInput = row.querySelector('.addon-input[data-section]');
        const rowSection = firstInput?.dataset.section || '';
        if (rowSection === section) {
          /*const customInputs = row.querySelectorAll('.addon-input');
          if (customInputs.length > 0) {
            // Trigger input event on all of them (or first one is enough if you want)
            customInputs.forEach(inp => inp.dispatchEvent(new Event('input', { bubbles: true })));
          } else {
            const sel = row.querySelector('select');
            if (sel && sel.value) sel.dispatchEvent(new Event('change', { bubbles: true }));
          }*/
          // Trigger input events on all manual inputs
          /*row.querySelectorAll('.manual-size-wrapper input').forEach(inp => {
            inp.dispatchEvent(new Event('input', { bubbles: true }));
          });

          // Trigger dropdown if no manual input
          const sel = row.querySelector('select');
          if (sel && sel.value) sel.dispatchEvent(new Event('change', { bubbles: true }));*/
          const sel = row.querySelector('.addon-dropdown');
          const customInputs = row.querySelectorAll('.addon-input');

          if (sel && sel.value) {
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          } else if (customInputs.length > 0) {
            customInputs.forEach(inp => inp.dispatchEvent(new Event('input', { bubbles: true })));
          }
        }
      });

      // Finally update total price
      updateTotalPrice();
    });
  });

  // ----------------------------
  // Pillow dropdown change
  // ----------------------------
  const pillowDropdown = document.querySelector('.pillow-dropdown');
  if (pillowDropdown) {
    pillowDropdown.addEventListener('change', function() {
      const wrapper = pillowDropdown.closest('.custom-pillow-wrap');
      const selectedOption = pillowDropdown.options[pillowDropdown.selectedIndex];
      const price = selectedOption ? parseFloat(selectedOption.dataset.price)*conversionRate || 0 : 0;

      // Update pillow price span
      const priceEl = wrapper.querySelector('.price-for-this-group-custom span');
      if (priceEl) priceEl.textContent = price.toFixed(2);

      // Update hidden input for Shopify properties
      const hiddenInput = wrapper.querySelector(`#hidden-pillow-Pillow`);
      if (hiddenInput) hiddenInput.value = pillowDropdown.value;

      // Update total
      updateTotalPrice();
    });
  }

  // ----------------------------
  // Napkin dropdown change
  // ----------------------------
  const napkinDropdown = document.querySelector('.napkin-dropdown');
  if (napkinDropdown) {
    // Reset total calculated price
    const calculatedPrice = document.getElementById('calculated-price');
    if (calculatedPrice) calculatedPrice.textContent = '0.00';
    napkinDropdown.addEventListener('change', function() {
      const wrapper = napkinDropdown.closest('.custom-napkin-wrap');
      const selectedOption = napkinDropdown.options[napkinDropdown.selectedIndex];
      // Get selected value (number)
      const selectedValue = parseFloat(napkinDropdown.value) || 0;

      // Get price from data attribute
      const pricePerUnit = parseFloat(selectedOption.dataset.price) || 0;

      // Multiply value × price
      const totalPrice = selectedValue * pricePerUnit * (conversionRate || 1);

      // Update price display
      const priceEl = wrapper.querySelector('.price-for-this-group-custom span');
      if (priceEl) priceEl.textContent = totalPrice.toFixed(2);

      // Update hidden input (optional)
      const hiddenInput = wrapper.querySelector(`#hidden-napkin-size`);
      if (hiddenInput) hiddenInput.value = '50x50cm';

      // Update total
      updateTotalPrice();
    });
  }

  // --- Shape selector click ---
  document.querySelectorAll(".custom-shape-selector-custom .shape").forEach(shape => {
    shape.addEventListener("click", () => {
      const shapeId = shape.dataset.id;
      const numDimensions = shape.dataset.dimensions;

      // Remove 'active' from all
      document.querySelectorAll(".custom-shape-selector-custom .shape").forEach(s => 
        s.classList.remove("active")
      );

      // Add 'active' to clicked one
      shape.classList.add("active");

      console.log(numDimensions, "shapeID");
      const priceSpan = document.querySelector(
        ".price-for-this-group-custom.rv-boated span"
      );
      if (priceSpan) {
        console.log("priceSpan", priceSpan);
        priceSpan.textContent = 0;
      }

      const PRICE_OUT = document.querySelector('#calculated-price');

      if (PRICE_OUT) {
        console.log("price");
        PRICE_OUT.textContent = 0;
      }
      updateSubmitStateNew();

      // Update shape details
      updateShapeDetails(shapeId, numDimensions);
    });
  });

  // --- Input change listener for shape dimensions ---
  /*document.addEventListener("input", event => {
    // Check if the input is inside #dynamic-inputs within .custom-shape-selector-custom
    if (event.target.closest(".custom-shape-selector-custom #dynamic-inputs input")) {
      const parent = event.target.closest(".custom-shape-selector-custom");
      const dynamicInputs = parent.querySelector("#dynamic-inputs");
      const shapeValues = parent.querySelector("#shape-values");

      // Collect all input values
      const values = Array.from(dynamicInputs.querySelectorAll("input")).map(input => {
        const value = input.value || 0;
        return value + " in";
      });

      // Update #shape-values hidden field
      if (shapeValues) shapeValues.value = values.join(" x ");

      // ✅ Trigger your submit state function
      updateSubmitStateNew();
    }
  });*/


  // --- Update shape inputs ---
  function updateShapeDetails(shapeId, numDimensions) {
    const letters = "abcdefghijklmnopqrstuvwxyz".split("");
    const parent = document.querySelector(".custom-shape-selector-custom");
    const shapeElement = parent.querySelector(`.shape[data-id='${shapeId}']`);
    if (!shapeElement) return;

    const dimensions = shapeElement.dataset.dimensions;
    const imageSrc = shapeElement.dataset.image;

    const config = shapesConfig[shapeId];

    const selectedUnit = document.querySelector('input[name="dimension_unit"]:checked')?.value || 'cm';
    const unitLabel = selectedUnit === 'cm' ? 'cm.' : 'in.';

    let sideInputs = "";
    for (let i = 0; i < dimensions; i++) {
      sideInputs += `
        <div class="multi-shapes-wrap">
          <p>Side ${letters[i]}</p>
          <input type="number" class="side-${letters[i]} comman-cl-all-shape" placeholder="00" />
          <span class="dimension-unit-show">${unitLabel}</span>
        </div>`;
    }

    const dynamicInputs = parent.querySelector("#dynamic-inputs");

    const shapeName = parent.querySelector("#shape-name");
    const shapeImg = parent.querySelector(".selected-shape-wrap img");
    const shapeValues = parent.querySelector("#shape-values");

    if (dynamicInputs) dynamicInputs.innerHTML = sideInputs;
    if (shapeName) shapeName.textContent = `Shape ${shapeId}`;
    if (shapeImg) shapeImg.src = imageSrc;

    // Update #shape-values on input
    if (dynamicInputs) {
      dynamicInputs.querySelectorAll("input").forEach(input => {
        input.addEventListener("input", () => {
          const values = Array.from(dynamicInputs.querySelectorAll("input")).map(i => {
            const value = i.value || 0;
            return value + " in";
          });

          // const shapeWidthLengthMap = {
          //     1: [0, 1], // shape 1 has 2 dims → width=0, length=1
          //     2: [0, 1], // shape 2 has 2 dims → width=0, length=1
          //     3: [2, 3], // shape 3 has 3 dims → width=0, length=1
          //     4: [2, 1], // shape 4 has 3 dims → width=0, length=1
          //     5: [0, 1], // shape 5 has 4 dims → width=0, length=1
          //     6: [0, 1], // shape 6 has 2 dims → width=0, length=1
          //     7: [0, 1], // shape 7 has 3 dims → width=0, length=1
          //     8: [0, 1], // shape 8 has 4 dims → width=0, length=1
          //     9: [3, 1], // shape 9 has 2 dims → width=0, length=1
          //     10: [0, 1], // shape 10 has 3 dims → width=0, length=1
          //     11: [2, 1], // shape 10 has 3 dims → width=0, length=1
          //     12: [3, 1], // shape 10 has 3 dims → width=0, length=1
          //     13: [0, 1], // shape 10 has 3 dims → width=0, length=1
          //     14: [0, 1], // shape 10 has 3 dims → width=0, length=1
          //     15: [0, 1], // shape 10 has 3 dims → width=0, length=1
          //     16: [0, 1], // shape 10 has 3 dims → width=0, length=1
          //   };

          // const indices = shapeWidthLengthMap[shapeId] || [];
          // // const firstTwoNumbers = values
          // //   .slice(0, 2)
          // //   .map(v => parseFloat(v));
          // const firstTwoNumbers = indices.map(i => parseFloat(values[i]) || 0);
          // const productWrapper =
          // dynamicInputs.closest(".product-form") ||
          // dynamicInputs.closest("form") ||
          // dynamicInputs.closest("[data-section]");


          // Convert values array into side object
          const sides = {
            A: parseFloat(values[0]) || 0,
            B: parseFloat(values[1]) || 0,
            C: parseFloat(values[2]) || 0,
            D: parseFloat(values[3]) || 0,
            E: parseFloat(values[4]) || 0,
          };

          // Mapping based on your sheet
          const shapeWidthLengthMap = {
            1:  { width: ["A"], length: ["B"] },
            2:  { width: ["A"], length: ["B"] },
            3:  { width: ["C"], length: ["D"] },
            4:  { width: ["C"], length: ["B"] },
            5:  { width: ["A"], length: ["D"] },
            6:  { width: ["A"], length: ["B"] },
            7:  { width: ["A"], length: ["B"] },
            8:  { width: ["A"], length: ["B"] },
            9:  { width: ["D"], length: ["B", "C"] },
            10: { width: ["A"], length: ["B", "C"] },
            11: { width: ["C"], length: ["D", "E"] },
            12: { width: ["D"], length: ["B", "C"] },
            13: { width: ["A"], length: ["D", "E"] },
            14: { width: ["A"], length: ["B", "C"] },
            15: { width: ["A"], length: ["B"] },
            16: { width: ["A"], length: ["B"] },
          };

          const config = shapeWidthLengthMap[shapeId];

          let calculatedWidth = 0;
          let calculatedLength = 0;

          if (config) {
            calculatedWidth = config.width.reduce(
              (sum, side) => sum + (sides[side] || 0),
              0
            );

            calculatedLength = config.length.reduce(
              (sum, side) => sum + (sides[side] || 0),
              0
            );
          }

          const productWrapper =
            dynamicInputs.closest(".product-form") ||
            dynamicInputs.closest("form") ||
            dynamicInputs.closest("[data-section]");

          const manualWidthInput = productWrapper?.querySelector(".manual-width");
          const manualLengthInput = productWrapper?.querySelector(".manual-length");

          if (manualWidthInput) {
            manualWidthInput.value = calculatedWidth || "";
            manualWidthInput.dispatchEvent(new Event("input", { bubbles: true }));
          }

          if (manualLengthInput) {
            manualLengthInput.value = calculatedLength || "";
            manualLengthInput.dispatchEvent(new Event("input", { bubbles: true }));
          }

          // const manualWidthInput = productWrapper?.querySelector(".manual-width");
          // const manualLengthInput = productWrapper?.querySelector(".manual-length");
          console.log(manualLengthInput.value, "manualLengthInput");
          console.log(manualWidthInput.value, "manualWidthInput");

          // if (manualWidthInput) {
          //   manualWidthInput.value = firstTwoNumbers[0] || "";
          //   manualWidthInput.dispatchEvent(
          //     new Event("input", { bubbles: true })
          //   );
          // }

          // if (manualLengthInput) {
          //   manualLengthInput.value = firstTwoNumbers[1] || "";
          //   manualLengthInput.dispatchEvent(
          //     new Event("input", { bubbles: true })
          //   );
          // }

          // console.log(firstTwoNumbers, "firstTwoNumbers");

          // console.log(values, "values------");
          if (shapeValues) shapeValues.value = values.join(" x ");
          updateSubmitStateNew();
        });
      });
    }

    updateSubmitStateNew();
  }

  // Code of Product Page Addon Selection Formula Start
  // ----------------------------
  // ⚡ EVENT BINDING for Addon Bedding (Top Sheet + Fitted Sheet)
  // ----------------------------
  /*document.querySelectorAll('.addon-input').forEach(input => {
    input.addEventListener('input', function() {
      const wrapper = input.closest('.addon-bedsheet-wrap');
      if (!wrapper) return;

      const widthEl = wrapper.querySelector('.addon-bedsheet-width');
      const lengthEl = wrapper.querySelector('.addon-bedsheet-length');
      const depthEl = wrapper.querySelector('.addon-bedsheet-depth');
      const priceEl = wrapper.querySelector('.price-for-this-group-custom span');
      const qtyEl = wrapper.querySelector('.addon-quantity');

      const width = parseFloat(widthEl?.value) || 0;
      const length = parseFloat(lengthEl?.value) || 0;
      const depth = parseFloat(depthEl?.value) || 0;
      const qty = parseInt(qtyEl?.value) || 1;
      const productType = input.dataset.productType;
      const section = input.dataset.section;

      console.log(`Section: ${section}, Product Type: ${productType}`);

      // Clear dropdown if manual input entered
      const dropdown = wrapper.querySelector('select');
      if (dropdown && (width || length || depth)) {
        dropdown.value = "";
      }

      let price = 0;

      if (section === 'Top-sheet') {
        price = calculatePriceAddonTopSheet(width, length, productType, qty);
      } else if (section === 'Fitted-sheet') {
        price = calculatePriceFittedSheet(width, length, depth, productType, qty);
      }

      console.log(price);

      priceEl.textContent = price;

      // Enable button if valid
      const addBtn = wrapper.querySelector('.addon_addtocart');
      if (price > 0) {
        addBtn.disabled = false;
        addBtn.classList.remove('is-disabled');
      } else {
        addBtn.disabled = true;
        addBtn.classList.add('is-disabled');
      }

      //updateTotalPrice();
    });
  });

  // ----------------------------
  // 🧮 CALCULATION FUNCTIONS
  // ----------------------------
  function calculatePriceAddonTopSheet(width, length, productType, qty = 1) {
    let perMeterPrice = variantPrice; // default
    switch (productType) {
      case 'Flat Bedding':
        return calcFlatBeddingAddonTopSheet(width, length, perMeterPrice);
      default:
        return 0;
    }
  }

  function calcFlatBeddingAddonTopSheet(widthIn, lengthIn, perMeterPrice) {
    if (!widthIn || !lengthIn) return 0.00;

    const F = inchesToCm(widthIn);
    const G = inchesToCm(lengthIn);

    const minDim = Math.min(F, G);
    const checkWidth1 = minDim + (2 * E);
    if (checkWidth1 > D) return 0;

    const maxDim = Math.max(F, G);
    const checkWidth2 = maxDim + (2 * E);
    const fabricInMeter = checkWidth2 / 100;
    const price = fabricInMeter * perMeterPrice;
    return price.toFixed(2);
  }*/
  // Code of Product Page Addon Selection Formula End
}

function getSelectedColorPrice(section) {
    // Find the checked input for that section
    const selectedColor = document.querySelector(`input.color-radio[data-section="${section}"]:checked`);

    if (selectedColor) {
      // Return the color price as a float
      return parseFloat(selectedColor.dataset.colorPrice || 1);
    }

    // If no color selected for that section, return 0
    return 1;
  }

function updateSubmitStateNew() {
  let allValid = true;

  // -------------------
  // Colors
  // -------------------
  if (document.querySelector('.color-radio')) {
    if (!document.querySelector('.color-radio:checked')) allValid = false;
  }

  // -------------------
  // Sizes (manual or dropdown)
  // -------------------
  document.querySelectorAll('.size-filled-custom').forEach(wrapper => {
    const dropdown = wrapper.querySelector('.size-dropdown');
    const dropdownVal = dropdown ? dropdown.value : '';

    // Get all manual inputs in this wrapper
    const manualInputs = wrapper.querySelectorAll('.manual-size-input');
    let allInputsFilled = true;
    manualInputs.forEach(input => {
      const val = input.value;
      console.log(val, "val");
      if (!val || parseFloat(val) <= 0) allInputsFilled = false;
    });

    // Section is valid if either dropdown selected OR all manual inputs filled
    if (!(dropdownVal || allInputsFilled)) allValid = false;
  });
  

  console.log(allValid+' - before custom field')

  // -------------------
  // Pillow
  // -------------------
  const pillowDropdown = document.querySelector('#pillow-dropdown');
  if (pillowDropdown && !pillowDropdown.value) allValid = false;

  // -------------------
  // Shape inputs
  // -------------------
  if (document.querySelector('.custom-shape-selector-custom')) {
    let allSidesFilled = true;
    document.querySelectorAll('#dynamic-inputs input').forEach(input => {
      if (!input.value || parseFloat(input.value) <= 0) allSidesFilled = false;
    });
    if (!allSidesFilled) allValid = false;
  }

  console.log(allValid+' - after all shape fields')

  // -------------------
  // ❌ Error messages check
  // -------------------
  /*document.querySelectorAll('.size-error-message').forEach(msg => {
    if (msg.textContent.trim() !== '') {
      allValid = false;
    }
  });*/
  document.querySelectorAll('.size-error-message').forEach(msg => {
    const type = msg.dataset?.type || ''; // 'error' or 'warning'
    const text = msg.textContent.trim();

    // Only mark invalid if it's an actual error
    if (type === 'error' && text !== '') {
      allValid = false;
    }
  });

  console.log(allValid+' - after error messages')

  // -------------------
  // Update Add to Cart button
  // -------------------
  const submitBtns = document.querySelectorAll('.product-form__submit');
  submitBtns.forEach(submit => {
    const btnText = submit.querySelector('span');
    if (allValid) {
      if (btnText) btnText.textContent = 'Add to cart';
      submit.disabled = false;
      submit.setAttribute('aria-disabled', 'false'); // ✅ enable
    } else {
      if (btnText) btnText.textContent = theme.strings.selectAllFields;
      submit.disabled = true;
      submit.setAttribute('aria-disabled', 'true'); // ✅ enable
    }
  });
}
// Code of Product Page Formula End


function tableCloths() {
  /*const shapeButtons = document.querySelectorAll(".shape-btn");
  const fieldsContainer = document.querySelector(".shape-fields-container");
  const hiddenInput = document.getElementById("hidden-size");

  const priceGroup = document.querySelector(".price-for-this-group-custom span");
  const selectedPrice = document.getElementById("calculated-price");

  shapeButtons.forEach(btn => {
    btn.addEventListener("click", () => {
      // Highlight active button
      shapeButtons.forEach(b => b.classList.remove("active"));
      btn.classList.add("active");

      const shape = btn.dataset.shape;
      hiddenInput.value = shape.charAt(0).toUpperCase() + shape.slice(1);

      // Reset prices
      if (priceGroup) priceGroup.textContent = "0.00";
      if (selectedPrice) selectedPrice.textContent = "0.00";

      // Clear previous fields
      fieldsContainer.innerHTML = "";

      if (shape === "rectangular") {
        fieldsContainer.innerHTML = `
          <div class="manual-size-wrapper rectangular-fields">
            <div class="manual-size-wrap">
              <span>Width</span>
              <input type="number" min="1" name="custom_width" placeholder="Width"
                class="manual-width manual-size-input"
                data-section="Rectangular-Table-Cloth" data-product-type="Table cloth">
              <span>in</span>
            </div>
            <div class="manual-size-wrap">
              <span>Length</span>
              <input type="number" min="1" name="custom_length" placeholder="Length"
                class="manual-length manual-size-input"
                data-section="Rectangular-Table-Cloth" data-product-type="Table cloth">
              <span>in</span>
            </div>
          </div>
        `;
      } else if (shape === "round") {
        fieldsContainer.innerHTML = `
          <div class="manual-size-wrapper round-fields">
            <div class="manual-size-wrap">
              <span>Diameter</span>
              <input type="number" min="1" name="custom_diameter" placeholder="Diameter"
                class="manual-diameter manual-size-input"
                data-section="Round-Table-Cloth" data-product-type="Table cloth">
              <span>in</span>
            </div>
          </div>
        `;
      }
    });
  });*/

  const shapeButtons = document.querySelectorAll(".shape-btn");
  const rectFields = document.querySelector(".rectangular-fields");
  const roundFields = document.querySelector(".round-fields");
  const hiddenInput = document.getElementById("hidden-size");

  const priceGroup = document.querySelector(".price-for-this-group-custom span");
  const selectedPrice = document.getElementById("calculated-price");

  shapeButtons.forEach(btn => {
    btn.addEventListener("click", () => {
      shapeButtons.forEach(b => b.classList.remove("active"));
      btn.classList.add("active");

      const shape = btn.dataset.shape;

      // Reset prices
      if (priceGroup) priceGroup.textContent = "0.00";
      if (selectedPrice) selectedPrice.textContent = "0.00";

      if (shape === "rectangular") {
        // Show rectangular, hide round
        rectFields.style.display = "flex";
        roundFields.style.display = "none";

        // Reset values to 0
        rectFields.querySelectorAll('input').forEach(input => input.value = 0);
        roundFields.querySelectorAll('input').forEach(input => input.value = 1);

        hiddenInput.value = "Rectangular";
      } else {
        // Show round, hide rectangular
        rectFields.style.display = "none";
        roundFields.style.display = "flex";

        // Reset values to 0
        rectFields.querySelectorAll('input').forEach(input => input.value = 1);
        roundFields.querySelectorAll('input').forEach(input => input.value = 0);

        hiddenInput.value = "Round";
      }

      updateSubmitStateNew();
    });
  });
}

// document.addEventListener("DOMContentLoaded", () => {
//   const productId =
//     window.meta?.product?.id ||
//     window.CUSTOM_PRODUCT_ID;

//   if (!productId) return;

//   const storageKey = `custom_dimensions_${productId}`;
//   const storedData = JSON.parse(localStorage.getItem(storageKey) || "{}");

//   if (!storedData || Object.keys(storedData).length === 0) return;

//   document.querySelectorAll('.size-filled-custom').forEach(wrapper => {
//     // First try wrapper.dataset.section, fallback to first input/select inside
//     let section = wrapper.dataset.section;
//     if (!section) {
//       const firstInput = wrapper.querySelector('.manual-size-input, .size-dropdown');
//       if (firstInput) {
//         section = firstInput.dataset.section;
//       }
//     }

//     if (!section || !storedData[section]) return;

//     const sectionData = storedData[section];

//     // Inputs
//     const widthEl = wrapper.querySelector('.manual-width');
//     const lengthEl = wrapper.querySelector('.manual-length');
//     const depthEl = wrapper.querySelector('.manual-depth');
//     const diameterEl = wrapper.querySelector('.manual-diameter');

//     // Restore values safely
//     if (widthEl && sectionData.width) widthEl.value = sectionData.width;
//     if (lengthEl && sectionData.length) lengthEl.value = sectionData.length;
//     if (depthEl && sectionData.depth) depthEl.value = sectionData.depth;
//     if (diameterEl && sectionData.diameter) diameterEl.value = sectionData.diameter;

//     // Restore dropdown if stored
//     const dropdown = wrapper.querySelector('.size-dropdown');
//     if (dropdown && sectionData.dropdownValue) {
//       dropdown.value = sectionData.dropdownValue;
//     }

//     // Trigger recalculation automatically
//     [widthEl, lengthEl, depthEl, diameterEl].forEach(input => {
//       if (input) input.dispatchEvent(new Event('input', { bubbles: true }));
//     });
//   });

// });

// function restoreCustomDimensions() {
//   const productId = window.meta?.product?.id || window.CUSTOM_PRODUCT_ID;
//   if (!productId) return;

//   const storageKey = `custom_dimensions_${productId}`;
//   const storedData = JSON.parse(localStorage.getItem(storageKey) || "{}");
//   if (!storedData || Object.keys(storedData).length === 0) return;

//   document.querySelectorAll('.size-filled-custom').forEach(wrapper => {
//     let section = wrapper.dataset.section;
//     if (!section) {
//       const firstInput = wrapper.querySelector('.manual-size-input, .size-dropdown');
//       if (firstInput) section = firstInput.dataset.section;
//     }
//     if (!section || !storedData[section]) return;

//     const sectionData = storedData[section];

//     const widthEl = wrapper.querySelector('.manual-width');
//     const lengthEl = wrapper.querySelector('.manual-length');
//     const depthEl = wrapper.querySelector('.manual-depth');
//     const diameterEl = wrapper.querySelector('.manual-diameter');

//     if (widthEl && sectionData.width) widthEl.value = sectionData.width;
//     if (lengthEl && sectionData.length) lengthEl.value = sectionData.length;
//     if (depthEl && sectionData.depth) depthEl.value = sectionData.depth;
//     if (diameterEl && sectionData.diameter) diameterEl.value = sectionData.diameter;

//     const dropdown = wrapper.querySelector('.size-dropdown');
//     if (dropdown && sectionData.dropdownValue) dropdown.value = sectionData.dropdownValue;

//     [widthEl, lengthEl, depthEl, diameterEl].forEach(input => {
//       if (input) input.dispatchEvent(new Event('input', { bubbles: true }));
//     });
//   });
// }


function setNativeValue(element, value) {
  if (!element) return;

  const valueSetter = Object.getOwnPropertyDescriptor(
    element.__proto__,
    'value'
  )?.set;

  if (valueSetter) {
    valueSetter.call(element, value);
  } else {
    element.value = value;
  }

  element.dispatchEvent(new Event('input', { bubbles: true }));
  element.dispatchEvent(new Event('change', { bubbles: true }));
}

function restoreCustomDimensions() {
  console.log("changeeee22");
  const productId = window.meta?.product?.id || window.CUSTOM_PRODUCT_ID;
  if (!productId) return;

  const unit = document.querySelector('input[name="dimension_unit"]:checked')?.value || 'in';

  initaddonUI();
  const storageKey = `custom_dimensions_${productId}`;
  const storedData = JSON.parse(localStorage.getItem(storageKey) || "{}");
  if (!storedData || Object.keys(storedData).length === 0) return;

  document.querySelectorAll('.size-filled-custom').forEach(wrapper => {
    let section = wrapper.dataset.section;

    if (!section) {
      const firstInput = wrapper.querySelector(
        '.manual-size-input, .size-dropdown'
      );
      if (firstInput) section = firstInput.dataset.section;
    }

    if (!section || !storedData[section]) return;

    const sectionData = storedData[section];

    const widthEl = wrapper.querySelector('.manual-width');
    const lengthEl = wrapper.querySelector('.manual-length');
    const depthEl = wrapper.querySelector('.manual-depth');
    const diameterEl = wrapper.querySelector('.manual-diameter');
    const dropdown = wrapper.querySelector('.size-dropdown');

    if (widthEl && sectionData.width) {
      setNativeValue(widthEl, sectionData.width);
    }

    if (lengthEl && sectionData.length) {
      setNativeValue(lengthEl, sectionData.length);
    }

    if (depthEl && sectionData.depth) {
      setNativeValue(depthEl, sectionData.depth);
    }

    if (diameterEl && sectionData.diameter) {
      setNativeValue(diameterEl, sectionData.diameter);
    }

    if (dropdown && sectionData.dropdownValue) {
      setNativeValue(dropdown, sectionData.dropdownValue);
    }
  });
}

/* Run AFTER theme JS is ready */
document.addEventListener('DOMContentLoaded', () => {
  setTimeout(restoreCustomDimensions, 300);
});

document.addEventListener('change', function (e) {
    if (!e.target.matches('input[name="dimension_unit"]')) return;

    const unit = e.target.value;

    document.querySelectorAll('.dimension-unit-show').forEach(function (el) {
      el.textContent = unit.toLowerCase() + '.';
    });
    console.log("changeeee");
    restoreCustomDimensions();
});


// function showWarningPopup(message) {

//   // prevent multiple popups
//   if (document.getElementById("sizeWarningPopup")) {
//     return;
//   }

//   // check localStorage
//   if (localStorage.getItem("hideSizeWarning") === "true") {
//     return;
//   }

//   const popup = document.createElement("div");
//   popup.id = "sizeWarningPopup";

//   popup.style.position = "fixed";
//   popup.style.top = "0";
//   popup.style.left = "0";
//   popup.style.width = "100%";
//   popup.style.height = "100%";
//   popup.style.background = "rgba(0,0,0,0.5)";
//   popup.style.display = "flex";
//   popup.style.alignItems = "center";
//   popup.style.justifyContent = "center";
//   popup.style.zIndex = "9999";

//   popup.innerHTML = `
//     <div style="background:#fff;padding:20px;border-radius:8px;width:400px;">
      
//       <div style="color:red;font-weight:bold;margin-bottom:15px;">
//         ${message}
//       </div>

//       <label style="display:block;margin-bottom:10px;">
//         <input type="checkbox" id="agreeCheckbox">
//         I understand and agree this order will be produced with multiple pieces
//       </label>

//       <label style="display:block;margin-bottom:20px;">
//         <input type="checkbox" id="dontShowCheckbox">
//         Don't show this message again
//       </label>

//       <div style="text-align:right;">
//         <button id="continueBtn" disabled style="margin-right:10px;padding:8px 12px;">
//           Continue
//         </button>

//         <button id="cancelBtn" style="padding:8px 12px;">
//           Cancel
//         </button>
//       </div>

//     </div>
//   `;

//   document.body.appendChild(popup);

//   const agree = popup.querySelector("#agreeCheckbox");
//   const dontShow = popup.querySelector("#dontShowCheckbox");
//   const continueBtn = popup.querySelector("#continueBtn");

//   // prevent user interaction behind popup
//   document.body.style.overflow = "hidden";

//   agree.onchange = function () {
//     continueBtn.disabled = !agree.checked;
//   };

//   continueBtn.onclick = function () {

//     if (dontShow.checked) {
//       localStorage.setItem("hideSizeWarning", "true");
//     }

//     closePopup();
//   };

//   popup.querySelector("#cancelBtn").onclick = closePopup;

//   function closePopup() {
//     document.body.style.overflow = "";
//     popup.remove();
//   }
// }

function showWarningPopup(message) {

  // Prevent multiple popups
  if (document.getElementById("multiplePiecePopup")) return;

  // Check localStorage
  if (localStorage.getItem("hideMultiplePieceWarning") === "true") return;

  const overlay = document.createElement("div");
  overlay.id = "multiplePiecePopup";

  overlay.innerHTML = `
    <div class="popup-box">

        <div class="popup-message">
            ${message}
        </div>

        <label class="popup-checkbox">
            <input type="checkbox" id="agreeCheckbox">
            ${CUSTOM_MESSAGES.multi_piece_agree}
        </label>

        <label class="popup-checkbox">
            <input type="checkbox" id="dontShowCheckbox">
            ${CUSTOM_MESSAGES.dont_show}
        </label>

        <div class="popup-buttons">
            <button id="continueBtn" disabled>${CUSTOM_MESSAGES.btn_continue}</button>
            <button id="cancelBtn">${CUSTOM_MESSAGES.btn_cancel}</button>
        </div>

    </div>
  `;

  document.body.appendChild(overlay);

  const agree = document.getElementById("agreeCheckbox");
  const continueBtn = document.getElementById("continueBtn");
  const cancelBtn = document.getElementById("cancelBtn");
  const dontShow = document.getElementById("dontShowCheckbox");

  agree.addEventListener("change", function () {
    continueBtn.disabled = !agree.checked;
  });

  continueBtn.onclick = function () {

    if (dontShow.checked) {
      localStorage.setItem("hideMultiplePieceWarning", "true");
    }

    overlay.remove();
  };

  cancelBtn.onclick = function () {

    document.querySelectorAll('.manual-size-input').forEach(input => {
      input.value = '';
    });

    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith('custom_dimensions_')) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));

    overlay.remove();
    location.reload();
  };
}